<template>
  <div class="optix-dashboard container-fluid p-0 m-0 mt-4">
    <div
      class="dashboard-card"
      :style="{ border: activeReportUrl ? 'none' : '' }"
    >
      <div
        v-if="!activeReportUrl"
        class="dashboard-header-row mb-3"
      >
        <div class="shrink-0">
          <h5 class="dashboard-title mb-1">
            Dashboard
          </h5>
          <p class="dashboard-subtitle mb-0">
            {{ trackedCount }} {{ trackedCount === 1 ? 'product' : 'products' }} {{ search.trim() ? 'found' : 'tracked' }}
          </p>
        </div>

        <div class="kpi-center-slot">
          <div
            v-if="!kpiCollapsed && kpiCards.length"
            class="kpi-cards-wrap"
          >
            <div class="flex items-center justify-center flex-wrap gap-1.5 min-w-0">
              <button
                v-for="(card, cardIdx) in kpiCards"
                :key="card.id"
                type="button"
                class="kpi-card"
                :class="kpiCardColorClass(card.percent)"
                data-toggle="tooltip"
                data-placement="bottom"
                data-html="true"
                data-boundary="window"
                :data-original-title="kpiCardTooltip(card)"
                @click="toggleKpiDisplay"
              >
                <span class="font-semibold tabular-nums">
                  <template v-if="kpiShowCount">{{ card.needCount }}/{{ card.total }}</template>
                  <template v-else>{{ card.percent }}%</template>
                </span>
                <span
                  class="font-medium"
                >{{ kpiCardLabels[cardIdx] }}</span>
              </button>
            </div>

            <div class="flex items-center gap-1">
              <button
                type="button"
                class="kpi-toggle-btn"
                title="Toggle percent / count"
                @click="toggleKpiDisplay"
              >
                {{ kpiShowCount ? '#' : '%' }}
              </button>

              <button
                type="button"
                class="kpi-collapse-btn"
                title="Collapse"
                @click="kpiCollapsed = true"
              >
                <ChevronUp
                  width="14"
                  height="14"
                />
              </button>
            </div>
          </div>

          <button
            v-else-if="kpiCollapsed && kpiCards.length"
            type="button"
            class="kpi-expand-btn"
            title="Show KPI rollup"
            @click="kpiCollapsed = false"
          >
            <span class="kpi-expand-badge">{{ kpiTotalNeedCount }}</span>
            Need optimization
            <ChevronDown
              class="kpi-expand-chevron"
              width="14"
              height="14"
            />
          </button>
        </div>

        <div class="dashboard-header-actions shrink-0">
          <div
            v-if="checkedProductIds.length === 0"
          >
            <button
              type="button"
              class="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 px-4 py-2 h-9 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm optimize-catalog-btn"
              @click="openOptimizeCatalogModal()"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-zap h-4 w-4"
              >
                <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
              </svg>
              Optimize
            </button>
          </div>

          <div
            v-else
            class="optimize-split-btn"
          >
            <button
              type="button"
              class="optimize-split-main"
              @click="openOptimizeCatalogModal(true)"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-zap h-4 w-4"
              >
                <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
              </svg>
              Optimize {{ checkedProductIds.length }} selected
            </button>
            <button
              type="button"
              class="optimize-split-clear"
              title="Clear selection"
              @click="clearCheckedProducts"
            >
              <X
                width="14"
                height="14"
              />
            </button>
          </div>

          <div
            v-if="!activeReportUrl"
            v-click-outside="() => closeFiltersPopover('header')"
            class="filters-popover-wrap"
          >
            <button
              type="button"
              class="btn btn-filters"
              :class="{ active: openFiltersMenu === 'header' }"
              @click="toggleFiltersPopover('header')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
              </svg>
              Filters
              <span
                v-if="activeFilterCount > 0"
                class="filter-count-badge"
              >{{ activeFilterCount }}</span>
            </button>

            <DashboardFiltersPopover
              v-if="openFiltersMenu === 'header'"
              v-bind="filtersPopoverProps"
              v-on="filtersPopoverListeners"
            />
          </div>

          <button
            type="button"
            class="btn btn-compare"
            :class="{ active: compareMode }"
            :aria-pressed="String(compareMode)"
            @click="compareMode = !compareMode"
          >
            <GitCompareArrows width="18" />
            Compare
          </button>

          <div class="view-mode-toggle">
            <button
              type="button"
              class="view-mode-btn"
              :class="{ active: viewMode === 'table' }"
              title="Table view"
              aria-label="Table view"
              @click="viewMode = 'table'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 3v18" />
                <rect
                  width="18"
                  height="18"
                  x="3"
                  y="3"
                  rx="2"
                />
                <path d="M3 9h18" />
                <path d="M3 15h18" />
              </svg>
            </button>
            <button
              type="button"
              class="view-mode-btn"
              :class="{ active: viewMode === 'chart' }"
              title="Chart view"
              aria-label="Chart view"
              @click="viewMode = 'chart'"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <line
                  x1="18"
                  x2="18"
                  y1="20"
                  y2="10"
                />
                <line
                  x1="12"
                  x2="12"
                  y1="20"
                  y2="4"
                />
                <line
                  x1="6"
                  x2="6"
                  y1="20"
                  y2="14"
                />
              </svg>
            </button>
          </div>

          <div class="search-outer">
            <div class="search-wrap">
              <i class="bi bi-search" />
              <textarea
                ref="searchInput"
                v-model="search"
                class="form-control search-input"
                placeholder="Search products..."
                rows="1"
                @input="resizeSearchInput"
              />
              <button
                v-if="search"
                type="button"
                class="search-clear-btn"
                @click="clearSearch"
              >
                <i class="bi bi-x-lg" />
              </button>
            </div>
            <span
              v-if="searchError"
              class="search-error-msg"
            >{{ searchError }}</span>
          </div>

          <div class="dropdown dashboard-actions-dropdown">
            <button
              class="btn btn-icon p-0 dropdown-toggle no-caret"
              type="button"
              data-toggle="dropdown"
              aria-haspopup="true"
              aria-expanded="false"
            >
              <i class="bi bi-three-dots" />
            </button>

            <div class="dropdown-menu dropdown-menu-right">
              <a
                class="dropdown-item"
                href="/admin/auth/settings/reports/dashboard"
                target="_blank"
                rel="noopener noreferrer"
              >
                Configure Dashboard...
              </a>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="openSendToEmailModal"
              >
                Send to Email
              </a>
              <a
                class="dropdown-item"
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                @click.prevent="updateScores"
              >
                Update Scores
              </a>
              <a
                class="dropdown-item"
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                @click.prevent="refreshReports"
              >
                Refresh Reports
              </a>
              <a
                class="dropdown-item"
                href="#"
                @click.prevent="download"
              >
                Download
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="activeReportUrl"
        id="reportDetailsContainer"
        class="report-details-container"
      >
        <div class="report-iframe-container">
          <div
            v-if="isReportLoading"
            id="reportLoadingOverlay"
            class="report-loading-overlay"
          >
            <div class="report-loading-content">
              <div
                class="spinner-border"
                role="status"
                style="width: 3rem; height: 3rem; margin-bottom: 1rem;"
              >
                <span class="sr-only">Loading...</span>
              </div>
              <div>Loading Report...</div>
            </div>
          </div>

          <iframe
            id="reportIframe"
            :src="activeReportUrl"
            class="report-iframe"
            frameborder="0"
            @load="handleIframeLoad"
          />
        </div>
      </div>

      <div
        v-if="compareMode && !activeReportUrl"
        class="dashboard-controls-row mt-4"
      >
        <div class="compare-controls">
          <div class="filter-inline compare-filter-inline">
            <span class="filter-label">Before</span>
            <select
              v-model="beforeRange"
              class="form-control filter-select"
            >
              <option value="1_week_ago">
                ~1 week ago
              </option>
              <option value="2_weeks_ago">
                ~2 weeks ago
              </option>
              <option value="30_days_ago">
                ~30 days ago
              </option>
              <option value="60_days_ago">
                ~60 days ago
              </option>
              <option value="90_days_ago">
                ~90 days ago
              </option>
              <option value="custom">
                Custom date...
              </option>
            </select>
          </div>

          <div
            v-if="beforeRange === 'custom'"
            v-click-outside="() => closeDatePicker('before')"
            class="date-picker compare-date-picker"
          >
            <div
              class="date-input-wrap"
              :class="{ 'is-open': activeDatePicker === 'before' }"
              @click="openDatePicker('before')"
            >
              <i class="bi bi-calendar3" />

              <input
                v-model="beforeCustomDate"
                type="text"
                class="form-control date-input"
                placeholder="Before date"
                readonly
              >

              <button
                v-if="beforeCustomDate"
                type="button"
                class="date-clear-btn"
                @click.stop="clearDatePickerValue('before')"
              >
                <i class="bi bi-x" />
              </button>
            </div>

            <div
              v-if="activeDatePicker === 'before'"
              class="date-picker-popover"
            >
              <div class="date-picker-header">
                <button
                  type="button"
                  class="date-nav-btn"
                  @click="goToPreviousMonth"
                >
                  <i class="bi bi-chevron-left" />
                </button>

                <div class="date-picker-title">
                  {{ currentMonthLabel }}
                </div>

                <button
                  type="button"
                  class="date-nav-btn"
                  @click="goToNextMonth"
                >
                  <i class="bi bi-chevron-right" />
                </button>
              </div>

              <div class="date-weekdays">
                <span
                  v-for="day in weekDays"
                  :key="day"
                >
                  {{ day }}
                </span>
              </div>

              <div class="date-days">
                <button
                  v-for="day in calendarDays"
                  :key="day.key"
                  type="button"
                  class="date-day"
                  :disabled="day.isDisabled"
                  :class="{
                    'is-muted': !day.isCurrentMonth,
                    'is-today': day.isToday,
                    'is-selected': day.isSelected,
                    'is-disabled': day.isDisabled
                  }"
                  @click="selectDatePickerValue(day.date)"
                >
                  {{ day.label }}
                </button>
              </div>
            </div>
          </div>

          <span class="compare-vs-label">vs</span>

          <div class="filter-inline compare-filter-inline">
            <select
              v-model="afterType"
              class="form-control filter-select"
            >
              <option value="Most Recent">
                Most Recent
              </option>
              <option value="Custom date...">
                Custom date...
              </option>
            </select>
          </div>

          <div
            v-if="afterType === 'Custom date...'"
            v-click-outside="() => closeDatePicker('after')"
            class="date-picker compare-date-picker"
          >
            <div
              class="date-input-wrap"
              :class="{ 'is-open': activeDatePicker === 'after' }"
              @click="openDatePicker('after')"
            >
              <i class="bi bi-calendar3" />

              <input
                v-model="afterCustomDate"
                type="text"
                class="form-control date-input"
                placeholder="After date"
                readonly
              >

              <button
                v-if="afterCustomDate"
                type="button"
                class="date-clear-btn"
                @click.stop="clearDatePickerValue('after')"
              >
                <i class="bi bi-x" />
              </button>
            </div>

            <div
              v-if="activeDatePicker === 'after'"
              class="date-picker-popover"
            >
              <div class="date-picker-header">
                <button
                  type="button"
                  class="date-nav-btn"
                  @click="goToPreviousMonth"
                >
                  <i class="bi bi-chevron-left" />
                </button>

                <div class="date-picker-title">
                  {{ currentMonthLabel }}
                </div>

                <button
                  type="button"
                  class="date-nav-btn"
                  @click="goToNextMonth"
                >
                  <i class="bi bi-chevron-right" />
                </button>
              </div>

              <div class="date-weekdays">
                <span
                  v-for="day in weekDays"
                  :key="day"
                >
                  {{ day }}
                </span>
              </div>

              <div class="date-days">
                <button
                  v-for="day in calendarDays"
                  :key="day.key"
                  type="button"
                  class="date-day"
                  :disabled="day.isDisabled"
                  :class="{
                    'is-muted': !day.isCurrentMonth,
                    'is-today': day.isToday,
                    'is-selected': day.isSelected,
                    'is-disabled': day.isDisabled
                  }"
                  @click="selectDatePickerValue(day.date)"
                >
                  {{ day.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="viewMode === 'table' && !activeReportUrl && showEmptyState"
        class="dashboard-empty-state mt-4"
      >
        <div class="empty-state-icon">
          <FolderOpen
            width="24"
            height="24"
          />
        </div>

        <h3 class="empty-state-title">
          {{ emptyStateTitle }}
        </h3>

        <div
          v-click-outside="() => closeFiltersPopover('empty-state')"
          class="empty-state-picker"
        >
          <button
            type="button"
            class="btn empty-state-btn"
            @click="toggleFiltersPopover('empty-state')"
          >
            <FolderOpen
              width="16"
              height="16"
            />
            Choose a product list
            <ChevronUp
              v-if="openFiltersMenu === 'empty-state'"
              width="16"
              height="16"
            />
            <ChevronDown
              v-else
              width="16"
              height="16"
            />
          </button>

          <DashboardFiltersPopover
            v-if="openFiltersMenu === 'empty-state'"
            class="empty-state-popover"
            v-bind="filtersPopoverProps"
            v-on="filtersPopoverListeners"
          />
        </div>
      </div>

      <div
        v-else-if="viewMode === 'table' && !activeReportUrl"
        ref="tableWrap"
        class="table-responsive dashboard-table-wrap mt-4"
      >
        <table class="table dashboard-table mb-0">
          <thead>
            <template v-if="isSingleReportHeader">
              <tr class="compact-head-row">
                <th class="check-col">
                  <input
                    type="checkbox"
                    :checked="allChecked"
                    aria-label="Select all products"
                    @change="toggleAllChecked"
                  >
                </th>

                <SortableTh
                  sort-key="product"
                  :active-key="sortKey"
                  :dir="sortDir"
                  class="product-col"
                  @sort="toggleSort"
                >
                  Product
                </SortableTh>

                <SortableTh
                  sort-key="optix"
                  :active-key="sortKey"
                  :dir="sortDir"
                  class="metric-col"
                  @sort="toggleSort"
                >
                  Optix™ Score
                </SortableTh>

                <template v-for="config in orderedConfigs">
                  <template v-if="config.is_pdp">
                    <SortableTh
                      v-if="compareMode"
                      :key="'single-before-group-' + config.id"
                      :sort-key="'before:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="metric-col group-divider-left"
                      @sort="toggleSort"
                    >
                      {{ formattedBeforeLabel }}
                    </SortableTh>
                    <SortableTh
                      :key="'single-text-group-' + config.id"
                      :sort-key="'text:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="metric-col"
                      :class="{ 'group-divider-left': !compareMode }"
                      @sort="toggleSort"
                    >
                      Text
                    </SortableTh>
                    <SortableTh
                      :key="'single-images-group-' + config.id"
                      :sort-key="'images:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="metric-col"
                      @sort="toggleSort"
                    >
                      Images
                    </SortableTh>
                    <SortableTh
                      v-if="compareMode"
                      :key="'single-delta-group-' + config.id"
                      :sort-key="'delta:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="delta-col"
                      @sort="toggleSort"
                    >
                      Δ
                    </SortableTh>
                  </template>

                  <template v-else>
                    <SortableTh
                      v-if="compareMode"
                      :key="'single-before-group-' + config.id"
                      :sort-key="'before:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="metric-col group-divider-left"
                      @sort="toggleSort"
                    >
                      {{ formattedBeforeLabel }}
                    </SortableTh>

                    <template v-if="getFlatConfigCustomFields(config).length">
                      <SortableTh
                        v-for="(field, fieldIndex) in getFlatConfigCustomFields(config)"
                        :key="'single-custom-field-head-' + config.id + '-' + field.key"
                        :sort-key="'custom:' + config.id + ':' + field.key"
                        :active-key="sortKey"
                        :dir="sortDir"
                        class="metric-col custom-field-child-head"
                        :class="{ 'group-divider-left': !compareMode && fieldIndex === 0 }"
                        @sort="toggleSort"
                      >
                        {{ field.label }}
                      </SortableTh>
                    </template>

                    <SortableTh
                      v-else
                      :key="'single-after-group-' + config.id"
                      :sort-key="'after:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="metric-col"
                      :class="{ 'group-divider-left': !compareMode }"
                      @sort="toggleSort"
                    >
                      {{ formattedAfterLabel }}
                    </SortableTh>

                    <SortableTh
                      v-if="compareMode"
                      :key="'single-delta-group-' + config.id"
                      :sort-key="'delta:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="delta-col"
                      @sort="toggleSort"
                    >
                      Δ
                    </SortableTh>
                  </template>
                </template>
              </tr>
            </template>

            <template v-else-if="isSingleGroupedReportHeader">
              <tr class="sub-head-row group-sub-head-row">
                <th
                  class="check-col"
                  rowspan="2"
                >
                  <input
                    type="checkbox"
                    :checked="allChecked"
                    aria-label="Select all products"
                    @change="toggleAllChecked"
                  >
                </th>

                <SortableTh
                  sort-key="product"
                  :active-key="sortKey"
                  :dir="sortDir"
                  rowspan="2"
                  class="product-col"
                  @sort="toggleSort"
                >
                  Product
                </SortableTh>

                <SortableTh
                  sort-key="optix"
                  :active-key="sortKey"
                  :dir="sortDir"
                  rowspan="2"
                  class="metric-col"
                  @sort="toggleSort"
                >
                  Optix™ Score
                </SortableTh>

                <template v-for="config in orderedConfigs">
                  <SortableTh
                    v-if="compareMode"
                    :key="'single-grouped-before-group-' + config.id"
                    :sort-key="'before:' + config.id"
                    :active-key="sortKey"
                    :dir="sortDir"
                    rowspan="2"
                    class="metric-col group-divider-left"
                    @sort="toggleSort"
                  >
                    {{ formattedBeforeLabel }}
                  </SortableTh>

                  <th
                    v-for="(group, groupIndex) in getConfigCustomFieldGroups(config)"
                    :key="'single-grouped-custom-field-group-head-' + config.id + '-' + group.key"
                    :colspan="group.children.length"
                    class="metric-col custom-field-group-head"
                    :class="{ 'group-divider-left': !compareMode && groupIndex === 0 }"
                  >
                    {{ group.label }}
                  </th>

                  <SortableTh
                    v-if="compareMode"
                    :key="'single-grouped-delta-group-' + config.id"
                    :sort-key="'delta:' + config.id"
                    :active-key="sortKey"
                    :dir="sortDir"
                    rowspan="2"
                    class="delta-col"
                    @sort="toggleSort"
                  >
                    Δ
                  </SortableTh>
                </template>
              </tr>

              <tr class="sub-head-row custom-field-sub-head-row">
                <template v-for="config in orderedConfigs">
                  <template v-for="group in getConfigCustomFieldGroups(config)">
                    <SortableTh
                      v-for="field in group.children"
                      :key="'single-grouped-custom-field-head-' + config.id + '-' + group.key + '-' + field.key"
                      :sort-key="'custom:' + config.id + ':' + field.key"
                      :active-key="sortKey"
                      :dir="sortDir"
                      class="metric-col custom-field-child-head"
                      @sort="toggleSort"
                    >
                      {{ field.label }}
                    </SortableTh>
                  </template>
                </template>
              </tr>
            </template>

            <template v-else>
              <tr class="top-head-row">
                <th
                  class="check-col"
                  rowspan="3"
                >
                  <input
                    type="checkbox"
                    :checked="allChecked"
                    aria-label="Select all products"
                    @change="toggleAllChecked"
                  >
                </th>

                <SortableTh
                  sort-key="product"
                  :active-key="sortKey"
                  :dir="sortDir"
                  rowspan="3"
                  class="product-col"
                  @sort="toggleSort"
                >
                  Product
                </SortableTh>

                <SortableTh
                  sort-key="optix"
                  :active-key="sortKey"
                  :dir="sortDir"
                  rowspan="3"
                  class="metric-col"
                  @sort="toggleSort"
                >
                  Optix™ Score
                </SortableTh>

                <th
                  v-for="config in orderedConfigs"
                  :key="'group-' + config.id"
                  :colspan="getConfigColspan(config)"
                  class="group-head group-divider-left"
                >
                  <div class="config-head-wrap">
                    <template v-if="config.icon">
                      <img
                        v-if="getConfigIconType(config) === 'image'"
                        :src="getConfigIconValue(config)"
                        :alt="`${config.display_name} icon`"
                        class="config-custom-icon mr-1"
                      >

                      <i
                        v-else-if="getConfigIconType(config) === 'lucide'"
                        :data-lucide="getConfigIconValue(config)"
                        class="config-lucide-icon text-muted mr-1"
                      />

                      <i
                        v-else
                        :class="[getConfigIconValue(config), 'text-muted', 'mr-1']"
                      />
                    </template>

                    <span>{{ config.display_name }}</span>
                  </div>
                </th>
              </tr>

              <tr class="sub-head-row group-sub-head-row">
                <template v-for="config in orderedConfigs">
                  <template v-if="config.is_pdp">
                    <SortableTh
                      v-if="compareMode"
                      :key="'before-group-' + config.id"
                      :sort-key="'before:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      rowspan="2"
                      class="metric-col group-divider-left"
                      @sort="toggleSort"
                    >
                      {{ formattedBeforeLabel }}
                    </SortableTh>
                    <SortableTh
                      :key="'text-group-' + config.id"
                      :sort-key="'text:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      rowspan="2"
                      class="metric-col"
                      :class="{ 'group-divider-left': !compareMode }"
                      @sort="toggleSort"
                    >
                      Text
                    </SortableTh>
                    <SortableTh
                      :key="'images-group-' + config.id"
                      :sort-key="'images:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      rowspan="2"
                      class="metric-col"
                      @sort="toggleSort"
                    >
                      Images
                    </SortableTh>
                    <SortableTh
                      v-if="compareMode"
                      :key="'delta-group-' + config.id"
                      :sort-key="'delta:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      rowspan="2"
                      class="delta-col"
                      @sort="toggleSort"
                    >
                      Δ
                    </SortableTh>
                  </template>

                  <template v-else>
                    <SortableTh
                      v-if="compareMode"
                      :key="'before-group-' + config.id"
                      :sort-key="'before:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      rowspan="2"
                      class="metric-col group-divider-left"
                      @sort="toggleSort"
                    >
                      {{ formattedBeforeLabel }}
                    </SortableTh>

                    <template v-if="hasGroupedCustomFields(config)">
                      <th
                        v-for="(group, groupIndex) in getConfigCustomFieldGroups(config)"
                        :key="'custom-field-group-head-' + config.id + '-' + group.key"
                        :colspan="group.children.length"
                        class="metric-col custom-field-group-head"
                        :class="{ 'group-divider-left': !compareMode && groupIndex === 0 }"
                      >
                        {{ group.label }}
                      </th>
                    </template>

                    <template v-else-if="getFlatConfigCustomFields(config).length">
                      <SortableTh
                        v-for="(field, fieldIndex) in getFlatConfigCustomFields(config)"
                        :key="'flat-custom-field-head-' + config.id + '-' + field.key"
                        :sort-key="'custom:' + config.id + ':' + field.key"
                        :active-key="sortKey"
                        :dir="sortDir"
                        rowspan="2"
                        class="metric-col custom-field-child-head"
                        :class="{ 'group-divider-left': !compareMode && fieldIndex === 0 }"
                        @sort="toggleSort"
                      >
                        {{ field.label }}
                      </SortableTh>
                    </template>

                    <SortableTh
                      v-else
                      :key="'after-group-' + config.id"
                      :sort-key="'after:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      rowspan="2"
                      class="metric-col"
                      :class="{ 'group-divider-left': !compareMode }"
                      @sort="toggleSort"
                    >
                      {{ formattedAfterLabel }}
                    </SortableTh>

                    <SortableTh
                      v-if="compareMode"
                      :key="'delta-group-' + config.id"
                      :sort-key="'delta:' + config.id"
                      :active-key="sortKey"
                      :dir="sortDir"
                      rowspan="2"
                      class="delta-col"
                      @sort="toggleSort"
                    >
                      Δ
                    </SortableTh>
                  </template>
                </template>
              </tr>

              <tr class="sub-head-row custom-field-sub-head-row">
                <template v-for="config in orderedConfigs">
                  <template v-if="!config.is_pdp && hasGroupedCustomFields(config)">
                    <template v-for="group in getConfigCustomFieldGroups(config)">
                      <SortableTh
                        v-for="field in group.children"
                        :key="'custom-field-head-' + config.id + '-' + group.key + '-' + field.key"
                        :sort-key="'custom:' + config.id + ':' + field.key"
                        :active-key="sortKey"
                        :dir="sortDir"
                        class="metric-col custom-field-child-head"
                        @sort="toggleSort"
                      >
                        {{ field.label }}
                      </SortableTh>
                    </template>
                  </template>
                </template>
              </tr>
            </template>
          </thead>

          <tbody>
            <tr
              v-for="item in filteredProducts"
              :key="item.rowKey"
              :data-selected="checkedSet.has(item.rowKey)"
            >
              <td class="check-col">
                <input
                  type="checkbox"
                  :checked="checkedSet.has(item.rowKey)"
                  :aria-label="'Select ' + item.product_name"
                  @change="toggleRowCheck(item.rowKey)"
                >
              </td>

              <td class="product-cell">
                <div class="d-flex align-items-center">
                  <div
                    class="product-thumb-wrap"
                    @mouseenter="item.image ? showImageZoom($event, item) : null"
                    @mousemove="item.image ? moveImageZoom($event) : null"
                    @mouseleave="hideImageZoom"
                  >
                    <img
                      v-if="item.image"
                      :src="item.image"
                      :alt="item.product_name"
                      class="product-thumb"
                    >

                    <div
                      v-else
                      class="product-thumb product-thumb-placeholder"
                    >
                      No image
                    </div>
                  </div>
                  <div class="product-meta">
                    <div
                      class="product-name"
                      :title="item.product_name"
                    >
                      {{ item.product_name }}
                    </div>

                    <div
                      v-if="getProductBrandLine(item)"
                      class="product-brand"
                      :title="getProductBrandLine(item)"
                    >
                      {{ getProductBrandLine(item) }}
                    </div>
                  </div>
                </div>
              </td>

              <td class="metric-cell">
                <span
                  class="score-pill"
                  :class="overallScoreLevelClass(item)"
                >
                  {{ formatPercent(item.optix_score) }}
                </span>
              </td>

              <template v-for="config in orderedConfigs">
                <template v-if="config.is_pdp">
                  <td
                    v-if="compareMode"
                    :key="'cell-before-' + item.rowKey + '-' + config.id"
                    class="metric-cell group-divider-left"
                  >
                    <a
                      v-if="item.metrics[config.id].beforeReportUrl"
                      href="#"
                      class="score-pill score-pill-light"
                      @click.prevent="openReportInFrame(
                        item.metrics[config.id].beforeReportUrl,
                        item.product_name,
                        item.metrics[config.id].beforeReportId || null,
                        item.metrics[config.id].beforeReportUuid || null
                      )"
                    >
                      {{ item.metrics[config.id].before }}
                    </a>
                    <span
                      v-else
                      class="score-pill score-pill-light"
                    >
                      -
                    </span>
                  </td>

                  <td
                    :key="'cell-text-' + item.rowKey + '-' + config.id"
                    class="metric-cell"
                    :class="{ 'group-divider-left': !compareMode }"
                  >
                    <a
                      v-if="item.metrics[config.id].textReportUrl"
                      href="#"
                      class="score-pill"
                      :class="scoreLevelClass(item.metrics[config.id].textNumeric)"
                      @click.prevent="openReportInFrame(
                        item.metrics[config.id].textReportUrl,
                        item.product_name,
                        item.metrics[config.id].textReportId || null,
                        item.metrics[config.id].textReportUuid || null
                      )"
                    >
                      {{ item.metrics[config.id].text }}
                    </a>
                    <span
                      v-else
                      class="score-pill"
                      :class="scoreLevelClass(item.metrics[config.id].textNumeric)"
                    >
                      {{ item.metrics[config.id].text }}
                    </span>
                  </td>

                  <td
                    :key="'cell-images-' + item.rowKey + '-' + config.id"
                    class="metric-cell"
                  >
                    <a
                      v-if="item.metrics[config.id].imagesReportUrl"
                      href="#"
                      class="score-pill"
                      :class="scoreLevelClass(item.metrics[config.id].imagesNumeric)"
                      @click.prevent="openReportInFrame(
                        item.metrics[config.id].imagesReportUrl,
                        item.product_name,
                        item.metrics[config.id].imagesReportId || null,
                        item.metrics[config.id].imagesReportUuid || null,
                        true
                      )"
                    >
                      {{ item.metrics[config.id].images }}
                    </a>
                    <span
                      v-else
                      class="score-pill"
                      :class="scoreLevelClass(item.metrics[config.id].imagesNumeric)"
                    >
                      {{ item.metrics[config.id].images }}
                    </span>
                  </td>

                  <td
                    v-if="compareMode"
                    :key="'cell-delta-' + item.rowKey + '-' + config.id"
                    class="delta-cell"
                  >
                    <span :class="deltaClass(item.metrics[config.id].delta)">
                      <i :class="deltaIcon(item.metrics[config.id].delta)" />
                      {{ abs(item.metrics[config.id].delta) }}
                    </span>
                  </td>
                </template>

                <template v-else>
                  <td
                    v-if="compareMode"
                    :key="'cell-before-' + item.rowKey + '-' + config.id"
                    class="metric-cell group-divider-left"
                  >
                    <a
                      v-if="item.metrics[config.id].beforeReportUrl"
                      href="#"
                      class="score-pill score-pill-light"
                      @click.prevent="openReportInFrame(
                        item.metrics[config.id].beforeReportUrl,
                        item.product_name,
                        item.metrics[config.id].beforeReportId || null,
                        item.metrics[config.id].beforeReportUuid || null
                      )"
                    >
                      {{ item.metrics[config.id].before }}
                    </a>
                    <span
                      v-else
                      class="score-pill score-pill-light"
                    >
                      —
                    </span>
                  </td>

                  <template v-if="item.metrics[config.id].customFields && item.metrics[config.id].customFields.length">
                    <td
                      v-for="(field, fieldIndex) in item.metrics[config.id].customFields"
                      :key="'cell-custom-' + item.rowKey + '-' + config.id + '-' + field.key"
                      class="metric-cell"
                      :class="{ 'group-divider-left': !compareMode && fieldIndex === 0 }"
                    >
                      <a
                        v-if="item.metrics[config.id].reportUrl"
                        href="#"
                        class="score-pill score-pill-light"
                        :class="customFieldPillClass(field)"
                        @click.prevent="openReportInFrame(
                          item.metrics[config.id].reportUrl,
                          item.product_name,
                          item.metrics[config.id].reportId || null,
                          item.metrics[config.id].reportUuid || null
                        )"
                      >
                        {{ field.value }}
                      </a>
                      <span
                        v-else
                        class="score-pill score-pill-light"
                        :class="customFieldPillClass(field)"
                      >
                        {{ field.value }}
                      </span>
                    </td>
                  </template>

                  <td
                    v-else
                    :key="'cell-after-' + item.rowKey + '-' + config.id"
                    class="metric-cell"
                    :class="{ 'group-divider-left': !compareMode }"
                  >
                    <a
                      v-if="item.metrics[config.id].reportUrl"
                      href="#"
                      target="_blank"
                      class="score-pill"
                      :class="scoreLevelClass(item.metrics[config.id].afterNumeric)"
                      @click.prevent="openReportInFrame(
                        item.metrics[config.id].reportUrl,
                        item.product_name,
                        item.metrics[config.id].reportId || null,
                        item.metrics[config.id].reportUuid || null
                      )"
                    >
                      {{ item.metrics[config.id].after }}
                    </a>
                    <span
                      v-else
                      class="score-pill"
                      :class="scoreLevelClass(item.metrics[config.id].afterNumeric)"
                    >
                      {{ item.metrics[config.id].after }}
                    </span>
                  </td>

                  <td
                    v-if="compareMode"
                    :key="'cell-delta-' + item.rowKey + '-' + config.id"
                    class="delta-cell"
                  >
                    <span :class="deltaClass(item.metrics[config.id].delta)">
                      <i :class="deltaIcon(item.metrics[config.id].delta)" />
                      {{ abs(item.metrics[config.id].delta) }}
                    </span>
                  </td>
                </template>
              </template>
            </tr>
          </tbody>
        </table>

        <div
          v-if="filteredProducts.length > 0 && (hasMoreProducts || isLoadingProducts || isLoadingMoreProducts)"
          class="dashboard-load-more-wrap"
        >
          <button
            type="button"
            class="btn btn-load-more"
            :disabled="isLoadingProducts || isLoadingMoreProducts || !hasMoreProducts"
            @click="loadMoreProducts"
          >
            <span
              v-if="isLoadingMoreProducts"
              class="spinner-border spinner-border-sm mr-2"
              role="status"
              aria-hidden="true"
            />
            {{ isLoadingMoreProducts ? 'Loading...' : 'Load more products' }}
          </button>

          <div class="load-more-meta">
            Showing {{ ajaxProducts.length }} of {{ productsPagination.total }}
          </div>
        </div>
      </div>

      <div
        v-else-if="viewMode !== 'table' && !activeReportUrl"
        class="chart-placeholder"
      >
        <ProductReportsScoreChart :products="filteredProducts" />
      </div>
    </div>
    <div
      v-if="showOptimizeCatalogModal"
      class="optimize-catalog-modal-overlay"
      @click.self="closeOptimizeCatalogModal"
    >
      <div
        role="dialog"
        aria-labelledby="optimizeCatalogTitle"
        class="optimize-catalog-modal"
      >
        <div class="pt-3 pb-3 border-b optimize-catalog-header">
          <div class="flex flex-col space-y-1 optimize-catalog-heading">
            <h2
              id="optimizeCatalogTitle"
              class="font-semibold text-lg optimize-catalog-title mb-0"
            >
              Optimize catalog
            </h2>
            <p class="text-muted-foreground text-sm optimize-catalog-subtitle">
              Pick the work, assign it, and optimize.
            </p>
          </div>

          <div class="mt-3 flex items-center gap-2 text-xs flex-wrap">
            <div class="inline-flex items-center rounded-md border bg-background p-0.5 h-8">
              <button
                type="button"
                class="border-0"
                :class="optimizeCatalogView === 'per_sku' ? 'h-7 px-2.5 text-xs rounded-[5px] font-medium transition-colors bg-emerald-600 text-white' : 'h-7 px-2.5 text-xs rounded-[5px] font-medium transition-colors text-muted-foreground hover:text-foreground'"
                @click="optimizeCatalogView = 'per_sku'"
              >
                Per SKU
              </button>
              <button
                type="button"
                class="border-0"
                :class="optimizeCatalogView === 'group' ? 'h-7 px-2.5 text-xs rounded-[5px] font-medium transition-colors bg-emerald-600 text-white' : 'h-7 px-2.5 text-xs rounded-[5px] font-medium transition-colors text-muted-foreground hover:text-foreground'"
                @click="optimizeCatalogView = 'group'"
              >
                Group
              </button>
            </div>

            <div
              v-click-outside="() => { showOptimizeCatalogDatePicker = false; }"
              class="relative"
            >
              <button
                type="button"
                class="inline-flex items-center whitespace-nowrap border border-input hover:bg-accent hover:text-accent-foreground rounded-md px-3 h-8 min-w-[130px] text-xs bg-background gap-1.5 justify-start font-normal"
                @click="showOptimizeCatalogDatePicker = !showOptimizeCatalogDatePicker"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-calendar h-3 w-3 text-muted-foreground shrink-0"
                >
                  <path d="M8 2v4" />
                  <path d="M16 2v4" />
                  <rect
                    width="18"
                    height="18"
                    x="3"
                    y="4"
                    rx="2"
                  />
                  <path d="M3 10h18" />
                </svg>
                {{ optimizeCatalogDueDateLabel }}
              </button>

              <div
                v-if="showOptimizeCatalogDatePicker"
                class="absolute top-full left-0 mt-1 z-50 rounded-md border bg-popover text-popover-foreground shadow-md w-auto p-0"
              >
                <div class="p-2 border-b flex flex-col gap-0.5">
                  <button
                    v-for="preset in ['This Friday', 'Next Monday', 'End of month', 'End of quarter']"
                    :key="preset"
                    type="button"
                    :class="optimizeCatalogDueDateLabel === preset ? 'bg-muted font-medium' : ''"
                    class="border-0 text-left text-xs px-2 py-1.5 rounded hover:bg-muted"
                    @click="setOptimizeCatalogDatePreset(preset)"
                  >
                    {{ preset }}
                  </button>
                </div>
                <div class="optimize-datepicker-cal">
                  <div class="optimize-datepicker-cal-header">
                    <button
                      type="button"
                      class="optimize-datepicker-cal-nav"
                      aria-label="Go to previous month"
                      @click="optimizeCatalogDatePickerPrev"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-left h-4 w-4"
                      >
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                    </button>
                    <span class="optimize-datepicker-cal-month">{{ optimizeCatalogDatePickerMonthLabel }}</span>
                    <button
                      type="button"
                      class="optimize-datepicker-cal-nav"
                      aria-label="Go to next month"
                      @click="optimizeCatalogDatePickerNext"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-chevron-right h-4 w-4"
                      >
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                  <div class="optimize-datepicker-cal-weekdays">
                    <span
                      v-for="d in ['Su','Mo','Tu','We','Th','Fr','Sa']"
                      :key="d"
                    >{{ d }}</span>
                  </div>
                  <div class="optimize-datepicker-cal-days">
                    <button
                      v-for="(day, idx) in optimizeCatalogDatePickerDays"
                      :key="idx"
                      type="button"
                      class="optimize-datepicker-cal-day"
                      :class="{
                        'is-other-month': !day.currentMonth,
                        'is-selected': day.isSelected,
                        'is-today': day.isToday
                      }"
                      @click="pickOptimizeCatalogDate(day)"
                    >
                      {{ day.day }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="optimizeCatalogView === 'per_sku'"
              v-click-outside="() => { showOptimizeCatalogToggleAll = false; }"
              class="relative"
            >
              <button
                type="button"
                class="inline-flex items-center justify-center whitespace-nowrap border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md px-3 h-8 text-xs gap-1.5 font-normal"
                @click="showOptimizeCatalogToggleAll = !showOptimizeCatalogToggleAll"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-list-checks h-3.5 w-3.5 text-muted-foreground"
                >
                  <path d="m3 17 2 2 4-4" />
                  <path d="m3 7 2 2 4-4" />
                  <path d="M13 6h8" />
                  <path d="M13 12h8" />
                  <path d="M13 18h8" />
                </svg>
                Toggle across all
                <span class="inline-flex items-center rounded-full border-transparent ml-1 h-4 px-1.5 text-[10px] font-semibold bg-emerald-100 text-emerald-700">
                  {{ optimizeCatalogTotalActiveCount }}
                </span>
              </button>

              <div
                v-if="showOptimizeCatalogToggleAll"
                class="absolute top-full left-0 mt-1 z-50 rounded-md border bg-popover text-popover-foreground shadow-md w-64 p-1.5"
              >
                <div class="flex flex-col">
                  <button
                    v-for="card in kpiCards"
                    :key="card.id"
                    type="button"
                    class="flex items-center gap-2 px-2 py-1.5 rounded border-0 hover:bg-muted text-left"
                    @click="toggleAllForIssue(card.id)"
                  >
                    <span
                      class="h-4 w-4 rounded border flex items-center justify-center shrink-0"
                      :class="optimizeCatalogToggleAllCounts[card.id] && optimizeCatalogToggleAllCounts[card.id].active > 0 ? 'bg-emerald-600 border-emerald-600' : 'bg-white border-input'"
                    >
                      <svg
                        v-if="optimizeCatalogToggleAllCounts[card.id] && optimizeCatalogToggleAllCounts[card.id].active === optimizeCatalogToggleAllCounts[card.id].total && optimizeCatalogToggleAllCounts[card.id].total > 0"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-check h-3 w-3 text-white"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                      <span
                        v-else-if="optimizeCatalogToggleAllCounts[card.id] && optimizeCatalogToggleAllCounts[card.id].active > 0"
                        class="h-0.5 w-2 bg-white rounded"
                      />
                    </span>
                    <span class="text-xs flex-1 truncate">{{ card.name }}</span>
                    <span class="text-[10px] text-muted-foreground shrink-0">
                      {{ optimizeCatalogToggleAllCounts[card.id] ? optimizeCatalogToggleAllCounts[card.id].active : 0 }}/{{ optimizeCatalogToggleAllCounts[card.id] ? optimizeCatalogToggleAllCounts[card.id].total : 0 }}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div
              v-click-outside="closeOptimizeDefaultOwnerDropdown"
              class="optimize-dropdown-wrap optimize-select-footer ml-auto"
            >
              <button
                type="button"
                class="optimize-dropdown-trigger optimize-dropdown-trigger-full optimize-select-wrap optimize-select-wrap-owner"
                @click="toggleOptimizeDefaultOwnerDropdown"
              >
                <i class="bi bi-person optimize-select-prefix" />
                <span class="optimize-dropdown-value">{{ getOptimizeAuthorName(optimizeCatalogDefaultOwnerId) }}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-chevron-down h-4 w-4 opacity-50 optimize-select-chevron"
                  aria-hidden="true"
                >
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              <div
                v-if="optimizeDefaultOwnerDropdownOpen"
                role="presentation"
                class="optimize-dropdown-menu p-1"
              >
                <button
                  v-for="author in optimizeCatalogAuthors"
                  :key="author.id"
                  type="button"
                  class="optimize-dropdown-option"
                  :class="{ 'is-selected': optimizeCatalogDefaultOwnerId === author.id }"
                  @click="selectOptimizeDefaultOwner(author.id)"
                >
                  <span class="optimize-dropdown-option-indicator">
                    <svg
                      v-if="optimizeCatalogDefaultOwnerId === author.id"
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-check h-4 w-4"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  </span>
                  <span>{{ author.fullName }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="optimizeCatalogView === 'per_sku'"
          class="px-6 py-3 overflow-y-auto space-y-1.5 optimize-catalog-issues-list"
        >
          <div
            v-for="product in optimizeCatalogModalProducts"
            :key="product.rowKey"
            class="w-full rounded-lg border transition-all px-3 py-2.5 border-emerald-500/50 bg-emerald-50/30"
          >
            <div class="flex items-center gap-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="lucide lucide-package h-4 w-4 shrink-0 text-emerald-700"
              >
                <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z" />
                <path d="M12 22V12" />
                <path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7" />
                <path d="m7.5 4.27 9 5.15" />
              </svg>
              <div class="flex-1 min-w-0 flex items-center gap-2">
                <span class="font-medium text-sm text-foreground truncate">{{ product.product_name }}</span>
                <div
                  v-if="getProductOverallScore(product) !== null"
                  class="inline-flex items-center rounded-full border-transparent text-[10px] font-medium shrink-0 px-1.5 py-0"
                  :class="getProductOverallScore(product) < 50 ? 'bg-red-100 text-red-700' : getProductOverallScore(product) < 85 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'"
                >
                  {{ getProductOverallScore(product) }}
                </div>
                <span
                  v-if="product.product_id && product.is_asin"
                  class="text-[11px] text-muted-foreground font-mono shrink-0"
                >{{ product.product_id }}</span>
              </div>
              <select
                class="flex items-center justify-between rounded-md border border-input px-2 py-1 h-7 w-[130px] text-[11px] bg-background shrink-0 border-dashed focus:outline-none"
                :value="optimizeCatalogSkuOwners[product.rowKey]"
                @change="$set(optimizeCatalogSkuOwners, product.rowKey, Number($event.target.value))"
              >
                <option
                  v-for="author in optimizeCatalogAuthors"
                  :key="author.id"
                  :value="author.id"
                >
                  {{ author.fullName }}
                </option>
              </select>
            </div>
            <div class="mt-2 flex items-center justify-end gap-1 flex-wrap">
              <button
                v-for="card in kpiCards"
                :key="card.id"
                type="button"
                :title="card.name"
                class="inline-flex items-center gap-1 h-6 px-1.5 rounded border text-[11px] whitespace-nowrap shrink-0 transition-colors"
                :class="optimizeCatalogSkuSelections[product.rowKey] && optimizeCatalogSkuSelections[product.rowKey][card.id] ? 'border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'border-border bg-background text-muted-foreground hover:text-foreground'"
                @click="toggleSkuIssue(product.rowKey, card.id)"
              >
                {{ card.name }}
              </button>
            </div>
          </div>
        </div>

        <div
          v-else
          class="px-6 py-3 overflow-y-auto space-y-1.5 optimize-catalog-issues-list"
        >
          <div
            v-for="issue in optimizeCatalogIssues"
            :key="issue.id"
            class="optimize-issue-row"
            :class="{'is-selected': issue.selected}"
          >
            <div class="flex items-center gap-3 optimize-issue-main-row">
              <button
                type="button"
                class="optimize-issue-check"
                :class="{'is-selected': issue.selected}"
                :aria-label="issue.selected ? 'Deselect issue' : 'Select issue'"
                @click="toggleOptimizeIssue(issue)"
              >
                <i
                  v-if="issue.selected"
                  class="bi bi-check"
                />
              </button>

              <button
                type="button"
                class="flex items-center gap-3 flex-1 min-w-0 text-left optimize-issue-content"
                @click="toggleOptimizeIssue(issue)"
              >
                <i
                  class="optimize-issue-icon"
                  :class="issue.iconClass"
                />

                <span class="flex-1 min-w-0">
                  <span class="flex items-center gap-2">
                    <span class="font-medium text-sm text-foreground truncate">{{ issue.name }}</span>
                    <span class="optimize-issue-count">
                      {{ issue.number }}
                    </span>
                  </span>
                </span>
              </button>

              <div
                v-click-outside="optimizeIssueOwnerDropdownId === issue.id ? closeOptimizeIssueOwnerDropdown : null"
                class="flex optimize-dropdown-wrap optimize-select-issue-owner"
              >
                <button
                  type="button"
                  class="optimize-dropdown-trigger optimize-dropdown-trigger-full optimize-select-wrap optimize-select-wrap-owner border-dashed"
                  @click="toggleOptimizeIssueOwnerDropdown(issue.id)"
                >
                  <i class="bi bi-person optimize-select-prefix" />
                  <span class="optimize-dropdown-value">{{ getOptimizeAuthorName(issue.authorId === null ? optimizeCatalogDefaultOwnerId : issue.authorId) }}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-chevron-down h-4 w-4 opacity-50 optimize-select-chevron"
                    aria-hidden="true"
                  >
                    <path d="m6 9 6 6 6-6" />
                  </svg>
                </button>

                <div
                  v-if="optimizeIssueOwnerDropdownId === issue.id"
                  role="presentation"
                  class="optimize-dropdown-menu p-1"
                  :style="optimizeDropdownStyle"
                >
                  <button
                    v-for="author in optimizeCatalogAuthors"
                    :key="author.id"
                    type="button"
                    class="optimize-dropdown-option"
                    :class="{ 'is-selected': (issue.authorId === null ? optimizeCatalogDefaultOwnerId : issue.authorId) === author.id }"
                    @click="selectOptimizeIssueOwner(issue, author.id)"
                  >
                    <span class="optimize-dropdown-option-indicator">
                      <svg
                        v-if="(issue.authorId === null ? optimizeCatalogDefaultOwnerId : issue.authorId) === author.id"
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        class="lucide lucide-check h-4 w-4"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    </span>
                    <span>{{ author.fullName }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="shrink-0 bg-border h-[1px] w-full" />

        <div class="px-6 py-3 bg-muted/30 flex items-center gap-3 flex-wrap optimize-catalog-footer">
          <div class="ml-auto flex items-center gap-2">
            <template v-if="optimizeCatalogView === 'per_sku'">
              <button
                type="button"
                class="optimize-btn-build-brief"
                :disabled="optimizeCatalogCreateBriefCount === 0"
                @click="openSkuBriefPreviewModal"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-file-text h-4 w-4"
                >
                  <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                  <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                  <path d="M10 9H8" />
                  <path d="M16 13H8" />
                  <path d="M16 17H8" />
                </svg>
                Create briefs ({{ optimizeCatalogCreateBriefCount }})
              </button>
              <button
                type="button"
                class="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 rounded-md px-3 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                :disabled="optimizeCatalogTotalActiveCount === 0"
                @click="optimizeWithAiPerSku"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-sparkles h-4 w-4"
                >
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  <path d="M20 3v4" />
                  <path d="M22 5h-4" />
                  <path d="M4 17v2" />
                  <path d="M5 18H3" />
                </svg>
                Optimize with AI
              </button>
            </template>
            <template v-else>
              <button
                type="button"
                class="optimize-btn-build-brief"
                :disabled="!optimizeCatalogSelectedIssues.length"
                @click="buildBrief"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-file-text h-4 w-4"
                >
                  <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
                  <path d="M14 2v4a2 2 0 0 0 2 2h4" />
                  <path d="M10 9H8" />
                  <path d="M16 13H8" />
                  <path d="M16 17H8" />
                </svg>
                Create brief
              </button>
              <button
                type="button"
                class="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-9 rounded-md px-3 gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                :disabled="!optimizeCatalogSelectedIssues.length"
                @click="optimizeWithAiGroup"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="lucide lucide-sparkles h-4 w-4"
                >
                  <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  <path d="M20 3v4" />
                  <path d="M22 5h-4" />
                  <path d="M4 17v2" />
                  <path d="M5 18H3" />
                </svg>
                Optimize with AI
              </button>
            </template>
          </div>
        </div>

        <button
          type="button"
          class="optimize-catalog-close"
          aria-label="Close"
          @click="closeOptimizeCatalogModal"
        >
          <i class="bi bi-x" />
        </button>
      </div>
    </div>

    <ProductReportsDashboardBriefModal
      ref="briefModal"
      :show-content-brief-modal="showContentBriefModal"
      :optimize-catalog-product-filter="optimizeCatalogProductFilter"
      :optimize-catalog-product-filters="optimizeCatalogProductFilterOptions"
      :optimize-catalog-issues="optimizeCatalogIssues"
      :total-product-count="selectedIssuesProductCount"
      :optimize-catalog-authors="optimizeCatalogAuthors"
      :optimize-catalog-default-owner-id="optimizeCatalogDefaultOwnerId"
      :optimize-catalog-due-date="optimizeCatalogDueDate"
      @close="closeContentBriefModal"
      @submit-brief="createBriefFromModal"
    />

    <ProductReportsDashboardSkuBriefModal
      v-if="showSkuBriefPreviewModal"
      :briefs="optimizeCatalogSkuBriefs"
      :is-submitting="isCreatingBriefs"
      :submit-results="skuBriefResults"
      @submit="createAllSkuBriefs"
      @close="closeSkuBriefPreviewModal"
    />

    <div
      v-if="showRefreshAllConfirm"
      class="refresh-confirm-overlay"
      @click.self="cancelRefreshAllReports"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="refreshAllConfirmTitle"
        class="refresh-confirm-modal"
      >
        <h5
          id="refreshAllConfirmTitle"
          class="refresh-confirm-title"
        >
          Refresh reports for the active prompts?
        </h5>

        <p class="refresh-confirm-text">
          Reports for the active dashboard prompts will be re-run across all lists,
          including products added directly by URL.
        </p>

        <div class="refresh-confirm-actions">
          <button
            type="button"
            class="refresh-confirm-btn refresh-confirm-btn--cancel"
            @click="cancelRefreshAllReports"
          >
            Cancel
          </button>
          <button
            ref="refreshConfirmBtn"
            type="button"
            class="refresh-confirm-btn refresh-confirm-btn--primary"
            @click="confirmRefreshAllReports"
          >
            Refresh all
          </button>
        </div>
      </div>
    </div>

    <div
      id="sendToEmailModal"
      class="modal fade optix-email-modal"
      tabindex="-1"
      role="dialog"
      aria-labelledby="sendToEmailModalLabel"
      aria-hidden="true"
    >
      <div
        class="modal-dialog modal-dialog-centered"
        role="document"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              id="sendToEmailModalLabel"
              class="modal-title"
            >
              <i class="bi bi-envelope-fill mr-2 text-info" />
              Send Optix Products Report
            </h5>

            <button
              type="button"
              class="close"
              aria-label="Close"
              @click="closeSendToEmailModal"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-group">
              <div class="custom-control custom-checkbox mb-2">
                <input
                  id="sendAllConfigs"
                  v-model="sendEmailForm.allConfigs"
                  type="checkbox"
                  class="custom-control-input"
                  @change="toggleAllConfigs"
                >
                <label
                  class="custom-control-label"
                  for="sendAllConfigs"
                >
                  All configs
                </label>
              </div>

              <label class="font-weight-bold">Configs:</label>

              <Multiselect
                v-model="sendEmailForm.selectedConfigs"
                :options="sendEmailConfigOptions"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :searchable="true"
                :show-labels="false"
                track-by="value"
                label="label"
                placeholder="Select one or more configs"
                class="send-email-multiselect"
                @input="handleSelectedConfigsChange"
              />
            </div>

            <div class="form-group">
              <div class="custom-control custom-checkbox mb-2">
                <input
                  id="sendOnlyIssueProducts"
                  v-model="sendEmailForm.onlyIssueProducts"
                  type="checkbox"
                  class="custom-control-input"
                >
                <label
                  class="custom-control-label"
                  for="sendOnlyIssueProducts"
                >
                  Only low-scoring products
                </label>
              </div>
            </div>

            <div
              v-if="sendEmailForm.onlyIssueProducts"
              class="form-group"
            >
              <label class="font-weight-bold">Threshold:</label>
              <div class="input-group">
                <input
                  v-model.number="sendEmailForm.threshold"
                  type="number"
                  min="0"
                  max="100"
                  class="form-control"
                  placeholder="70"
                >
                <div class="input-group-append">
                  <span class="input-group-text">%</span>
                </div>
              </div>
            </div>

            <div
              v-if="sendEmailForm.onlyIssueProducts"
              class="form-group"
            >
              <label class="font-weight-bold">Email summary text:</label>
              <textarea
                v-model="sendEmailForm.summaryTemplate"
                class="form-control send-email-textarea"
                rows="4"
                :placeholder="'Example: {{notOptimizedProducts}} of your {{totalProducts}} products are not mobile optimized'"
              />
              <small class="form-text text-muted">
                Available placeholders:
                <code v-text="'{{notOptimizedProducts}}'" />,
                <code v-text="'{{totalProducts}}'" />,
                <code v-text="'{{threshold}}'" />
              </small>
            </div>

            <div class="form-group">
              <label class="font-weight-bold">Email Recipients:</label>
              <textarea
                v-model="sendEmailForm.recipients"
                class="form-control send-email-textarea"
                rows="4"
                placeholder="Enter email addresses (one per line or separated by commas)"
              />
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-modal-cancel"
              @click="closeSendToEmailModal"
            >
              <i class="bi bi-x-lg mr-1" />
              Cancel
            </button>

            <button
              type="button"
              class="btn btn-modal-send"
              :disabled="isSendingEmail"
              @click="sendProductsReportEmail"
            >
              <i
                v-if="!isSendingEmail"
                class="bi bi-send mr-1"
              />
              <span
                v-if="isSendingEmail"
                class="spinner-border spinner-border-sm mr-2"
                role="status"
                aria-hidden="true"
              />
              {{ isSendingEmail ? 'Sending...' : 'Send Email' }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="imageZoom.visible"
      class="product-image-zoom"
      :style="{
        left: `${imageZoom.x}px`,
        top: `${imageZoom.y}px`
      }"
    >
      <img
        :src="imageZoom.src"
        :alt="imageZoom.alt"
      >
    </div>
  </div>
</template>

<script>
import Multiselect from 'vue-multiselect';
import {ChevronDown, ChevronUp, FolderOpen, GitCompareArrows, X} from "lucide-vue";
import ProductReportsScoreChart from "@frontend/components/common/dashboard/ProductReportsScoreChart.vue";
import SortableTh from "@frontend/components/common/dashboard/SortableTh.vue";
import ProductReportsDashboardBriefModal from "@frontend/components/common/dashboard/product-reports-dashboard/ProductReportsDashboardBriefModal.vue";
import ProductReportsDashboardSkuBriefModal from "@frontend/components/common/dashboard/product-reports-dashboard/ProductReportsDashboardSkuBriefModal.vue";
import DashboardFiltersPopover from "@frontend/components/common/dashboard/product-reports-dashboard/DashboardFiltersPopover.vue";
import { createStickyTableScroll } from "./utils/sticky-table-scroll";
import { removeUrlOriginLocation } from "@frontend/utils";
import debounce from 'lodash/debounce';

const NUMERIC_FIELD_BY_SORT_TYPE = {
  before: 'beforeNumeric',
  text: 'textNumeric',
  images: 'imagesNumeric',
  after: 'afterNumeric',
  delta: 'delta',
};

const SCORE_PASS_THRESHOLD = 85;
const SCORE_YELLOW_THRESHOLD = 65;

export default {
  name: 'ProductReportsDashboard',

  components: {ProductReportsScoreChart, Multiselect, ChevronDown, ChevronUp, FolderOpen, GitCompareArrows, X, SortableTh, ProductReportsDashboardBriefModal, ProductReportsDashboardSkuBriefModal, DashboardFiltersPopover},

  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__clickOutsideHandler__ = (event) => {
          if (!el.contains(event.target) && typeof binding.value === 'function') {
            binding.value(event);
          }
        };

        document.addEventListener('click', el.__clickOutsideHandler__);
      },

      unbind(el) {
        document.removeEventListener('click', el.__clickOutsideHandler__);
        delete el.__clickOutsideHandler__;
      },
    },
  },

  props: {
    products: {
      type: Array,
      default: () => ([])
    },
    configs: {
      type: Array,
      default: () => ([])
    },
    url: {
      type: String,
      required: true
    },
    companyId: {
      type: Number,
      required: true
    }
  },

  data() {
    return {
      search: '',
      totalTrackedProducts: 0,
      searchError: null,
      beforeRange: '1_week_ago',
      afterType: 'Most Recent',
      viewMode: 'table',
      openFiltersMenu: null,
      compareMode: false,
      checkedProductIds: [],
      kpiCollapsed: false,
      kpiShowCount: false,
      kpiSummary: null,
      kpiSummaryToken: 0,
      selectedRetailer: null,
      selectedCategory: null,
      selectedBrand: null,
      // retailerFilterOptions: [
      //   { value: 'all', label: 'All Retailers' },
      //   { value: 'amazon', label: 'Amazon' },
      //   { value: 'walmart', label: 'Walmart' },
      //   { value: 'home_depot', label: 'Home Depot' },
      //   { value: 'lowes', label: "Lowe’s" },
      // ],
      categoryFilterOptions: [
        { value: null, label: 'All Categories' },
        { value: 'hardware', label: 'Hardware' },
        { value: 'food', label: 'Food' },
        { value: 'beauty', label: 'Beauty' },
        { value: 'electronics', label: 'Electronics' },
      ],
      // brandFilterOptions: [
      //   { value: 'all', label: 'All Brands' },
      //   { value: 'ferrero', label: 'Ferrero' },
      //   { value: 'milwaukee', label: 'Milwaukee' },
      //   { value: 'dewalt', label: 'DeWalt' },
      //   { value: 'samsung', label: 'Samsung' },
      // ],
      ajaxProducts: [],
      productsPagination: {
        current_page: 1,
        last_page: 1,
        per_page: 100,
        total: 0,
      },
      isLoadingProducts: false,
      isLoadingMoreProducts: false,
      hasLoadedProducts: false,
      productsLoadError: false,
      activeReportUrl: null,
      activeReportName: null,
      activeReportId: null,
      activeReportUuid: null,
      isReportLoading: false,
      selectedProductList: null,
      selectedDashboardConfig: '',
      showIndividualItems: true,
      allProductLists: [],
      allProductListsLoaded: false,
      allProductListsLoadError: false,
      keepIndividualItems: false,
      isSendingEmail: false,
      sendEmailForm: {
        allConfigs: true,
        selectedConfigs: [],
        onlyIssueProducts: false,
        threshold: 70,
        summaryTemplate: '{{notOptimizedProducts}} of your {{totalProducts}} products are not mobile optimized',
        recipients: '',
        fields: ''
      },
      sortKey: null,
      sortDir: 'desc',
      isAfterDatePickerOpen: false,
      beforeCustomDate: '',
      afterCustomDate: '',
      activeDatePicker: null,
      calendarViewDate: new Date(),
      weekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
      imageZoom: {
        visible: false,
        src: '',
        alt: '',
        x: 0,
        y: 0,
      },
      showOptimizeCatalogModal: false,
      showContentBriefModal: false,
      showSkuBriefPreviewModal: false,
      optimizeCatalogView: 'per_sku',
      optimizeCatalogProductFilter: 'all',
      optimizeCatalogAuthors: [
        { id: 1, fullName: 'Sarah K.' },
        { id: 2, fullName: 'Marcus L.' },
        { id: 3, fullName: 'Priya N.' },
        { id: 4, fullName: 'Agency (Edelman)' },
        { id: 5, fullName: 'It’sRapid' },
      ],
      optimizeCatalogDefaultOwnerId: 1,
      optimizeCatalogPeriod: 'This Friday',
      optimizeCatalogDueDate: '',
      optimizeCatalogDatePickerView: new Date(),
      showOptimizeCatalogDatePicker: false,
      showOptimizeCatalogToggleAll: false,
      optimizeCatalogGroupName: '',
      optimizeCatalogSkuSelections: {},
      optimizeCatalogSkuOwners: {},
      optimizeCatalogSkuBriefs: [],
      isCreatingBriefs: false,
      skuBriefResults: [],
      optimizeCatalogIssues: [
        { id: 'add-decision-imagery', name: 'Add decision imagery', number: 6, iconClass: 'bi bi-grid', authorId: null, ownerTouched: false, selected: false },
        { id: 'improve-aeo-geo', name: 'Improve AEO/GEO', number: 6, iconClass: 'bi bi-search', authorId: null, ownerTouched: false, selected: false },
        { id: 'rewrite-titles-bullets', name: 'Rewrite titles & bullets', number: 5, iconClass: 'bi bi-type', authorId: null, ownerTouched: false, selected: false },
        { id: 'refresh-imagery', name: 'Refresh imagery', number: 4, iconClass: 'bi bi-image', authorId: null, ownerTouched: false, selected: true },
      ],
      optimizeIssueOwnerDropdownId: null,
      optimizeDefaultOwnerDropdownOpen: false,
      optimizeProductDropdownOpen: false,
      optimizeDropdownStyle: {},
      showRefreshAllConfirm: false,
      dashboardConfigs: Array.isArray(this.configs) ? [...this.configs] : [],
    };
  },

  computed: {
    retailerFilterOptions() {
      const retailers = this.normalizedProducts
        .map((product) => this.getRetailerFromProduct(product))
        .filter(Boolean);

      return [
        {
          value: null,
          label: 'All Retailers',
        },
        ...[...new Set(retailers)]
          .sort((a, b) => a.localeCompare(b))
          .map((retailer) => ({
            value: retailer,
            label: retailer,
          })),
      ];
    },

    brandFilterOptions() {
      const brands = this.normalizedProducts
        .map((product) => String(product.product_brand || '').trim())
        .filter(Boolean);

      return [
        {
          value: null,
          label: 'All Brands',
        },
        ...[...new Set(brands)]
          .sort((a, b) => a.localeCompare(b))
          .map((brand) => ({
            value: brand,
            label: brand,
          })),
      ];
    },
    isSingleReportHeader() {
      if (this.orderedConfigs.length !== 1) {
        return false;
      }

      const [config] = this.orderedConfigs;

      return !this.hasGroupedCustomFields(config);
    },

    isSingleGroupedReportHeader() {
      if (this.orderedConfigs.length !== 1) {
        return false;
      }

      const [config] = this.orderedConfigs;

      return !Number(config.is_pdp) && this.hasGroupedCustomFields(config);
    },

    currentMonthLabel() {
      return this.calendarViewDate.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    },

    calendarDays() {
      const year = this.calendarViewDate.getFullYear();
      const month = this.calendarViewDate.getMonth();

      const firstDayOfMonth = new Date(year, month, 1);
      const startDate = new Date(firstDayOfMonth);
      startDate.setDate(startDate.getDate() - firstDayOfMonth.getDay());

      const today = new Date();
      const selectedDate = this.parseDate(this.activeDatePickerValue);

      return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + index);

        return {
          key: `${this.activeDatePicker || 'none'}-${date.toISOString()}`,
          date,
          label: date.getDate(),
          isCurrentMonth: date.getMonth() === month,
          isToday: this.isSameDate(date, today),
          isSelected: selectedDate ? this.isSameDate(date, selectedDate) : false,
          isDisabled: this.isDateDisabledForActivePicker(date),
        };
      });
    },
    activeDatePickerValue() {
      if (this.activeDatePicker === 'before') {
        return this.beforeCustomDate;
      }

      if (this.activeDatePicker === 'after') {
        return this.afterCustomDate;
      }

      return '';
    },
    formattedBeforeLabel() {
      const labels = {
        recent: 'Recent',
        '1_week_ago': '~1 week ago',
        '2_weeks_ago': '~2 weeks ago',
        '30_days_ago': '~30 days ago',
        '60_days_ago': '~60 days ago',
        '90_days_ago': '~90 days ago',
      };

      if (this.beforeRange === 'custom') {
        return this.beforeCustomDate || 'Custom date...';
      }

      return labels[this.beforeRange] || this.beforeRange;
    },

    selectedIssuesProductCount() {
      return this.optimizeCatalogIssues
        .filter(issue => issue.selected)
        .reduce((total, issue) => total + (issue.number || 0), 0);
    },

    optimizeCatalogProductFilters() {
      const all = this.filteredProducts.length;
      const failing = this.filteredProducts.filter(p => this.isProductFailing(p)).length;
      const warningOrFail = this.filteredProducts.filter(p => this.isProductWarningOrFailing(p)).length;
      return [
        { value: 'all', label: `All products (${all})` },
        { value: 'failing', label: `Failing only (${failing})` },
        { value: 'warning_fail', label: `Warning + Fail (${warningOrFail})` },
      ];
    },

    optimizeCatalogModalProducts() {
      const filter = this.optimizeCatalogProductFilter;
      if (filter === 'selected') {
        return this.filteredProducts.filter(p => this.checkedSet.has(p.rowKey));
      }
      if (filter === 'failing') {
        return this.filteredProducts.filter(p => this.isProductFailing(p));
      }
      if (filter === 'warning_fail') {
        return this.filteredProducts.filter(p => this.isProductWarningOrFailing(p));
      }
      return this.filteredProducts;
    },

    optimizeCatalogSelectionStats() {
      const cardCounts = {};
      const total = this.optimizeCatalogModalProducts.length;
      for (const card of this.kpiCards) {
        cardCounts[card.id] = { active: 0, total };
      }
      let totalActive = 0;
      let briefCount = 0;
      for (const product of this.optimizeCatalogModalProducts) {
        const sel = this.optimizeCatalogSkuSelections[product.rowKey];
        if (!sel) continue;
        let productHasActive = false;
        for (const card of this.kpiCards) {
          if (sel[card.id]) {
            cardCounts[card.id].active++;
            totalActive++;
            productHasActive = true;
          }
        }
        if (productHasActive) briefCount++;
      }
      return { cardCounts, totalActive, briefCount };
    },

    optimizeCatalogToggleAllCounts() {
      return this.optimizeCatalogSelectionStats.cardCounts;
    },

    optimizeCatalogTotalActiveCount() {
      return this.optimizeCatalogSelectionStats.totalActive;
    },

    optimizeCatalogCreateBriefCount() {
      return this.optimizeCatalogSelectionStats.briefCount;
    },

    optimizeCatalogDueDateLabel() {
      if (!this.optimizeCatalogDueDate) return 'Pick a date';
      const presets = ['This Friday', 'Next Monday', 'End of month', 'End of quarter'];
      for (const label of presets) {
        if (this.computePresetDate(label) === this.optimizeCatalogDueDate) return label;
      }
      const [y, m, d] = this.optimizeCatalogDueDate.split('-');
      const dt = new Date(Number(y), Number(m) - 1, Number(d));
      return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    optimizeCatalogDatePickerMonthLabel() {
      return this.optimizeCatalogDatePickerView.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    },

    optimizeCatalogDatePickerDays() {
      const view = this.optimizeCatalogDatePickerView;
      const year = view.getFullYear();
      const month = view.getMonth();
      const firstDay = new Date(year, month, 1).getDay();
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      const daysInPrevMonth = new Date(year, month, 0).getDate();
      const today = new Date();
      const selectedStr = this.optimizeCatalogDueDate;
      const days = [];

      for (let i = firstDay - 1; i >= 0; i--) {
        const d = daysInPrevMonth - i;
        days.push({ day: d, currentMonth: false, date: new Date(year, month - 1, d), isSelected: false, isToday: false });
      }
      for (let d = 1; d <= daysInMonth; d++) {
        const date = new Date(year, month, d);
        const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
        days.push({ day: d, currentMonth: true, date, isSelected: dateStr === selectedStr, isToday: date.toDateString() === today.toDateString() });
      }
      const remaining = 42 - days.length;
      for (let d = 1; d <= remaining; d++) {
        days.push({ day: d, currentMonth: false, date: new Date(year, month + 1, d), isSelected: false, isToday: false });
      }
      return days;
    },

    hasClientSideFilters() {
      return !!(this.selectedRetailer || this.selectedBrand || this.selectedCategory);
    },

    showEmptyState() {
      return this.hasLoadedProducts
        && !this.searchError
        && this.filteredProducts.length === 0;
    },

    emptyStateTitle() {
      if (this.productsLoadError) {
        return 'Could not load products. Please try again.';
      }

      if (this.ajaxProducts.length) {
        return 'No products match these filters';
      }

      if (this.search.trim()) {
        return 'No products match your search';
      }

      return 'Select a product list to populate the dashboard';
    },

    orderedProductLists() {
      const lists = Array.isArray(this.allProductLists) ? this.allProductLists.slice() : [];

      // "Individual items" is pinned above this list in the popover, so the curated
      // "Dashboard" list comes right after it and every other list keeps its own order.
      return [
        ...lists.filter(list => this.isDashboardProductList(list)),
        ...lists.filter(list => !this.isDashboardProductList(list)),
      ];
    },

    filtersPopoverProps() {
      return {
        configs: Array.isArray(this.dashboardConfigs) ? this.dashboardConfigs : this.configs,
        dashboardConfig: this.selectedDashboardConfig,
        productLists: this.orderedProductLists,
        productListsLoaded: this.allProductListsLoaded,
        productListsLoadError: this.allProductListsLoadError,
        selectedLists: this.selectedProductList || [],
        individualItems: this.showIndividualItems,
        retailer: this.selectedRetailer,
        category: this.selectedCategory,
        brand: this.selectedBrand,
        retailerOptions: this.retailerFilterOptions,
        categoryOptions: this.categoryFilterOptions,
        brandOptions: this.brandFilterOptions,
        loading: this.isLoadingProducts,
      };
    },

    filtersPopoverListeners() {
      return {
        'update:individualItems': value => { this.showIndividualItems = value; },
        'update:dashboardConfig': value => { this.selectedDashboardConfig = value; },
        'update:retailer': value => { this.selectedRetailer = value; },
        'update:category': value => { this.selectedCategory = value; },
        'update:brand': value => { this.selectedBrand = value; },
        'toggle-list': this.toggleProductListItem,
        'select-all': this.selectAllProductListsInPopover,
        'clear-all': this.clearAllProductListsInPopover,
      };
    },

    kpiCards() {
      const allProducts = this.filteredProducts;

      if (!allProducts.length || !this.orderedConfigs.length) {
        return [];
      }

      const selectedKeys = new Set(this.checkedProductIds);
      const products = selectedKeys.size
        ? allProducts.filter(product => selectedKeys.has(product.rowKey))
        : allProducts;

      if (!products.length) {
        return [];
      }

      // Use server summary when available and no row selection or client-side filters are active.
      if (this.kpiSummary && !selectedKeys.size && !this.hasClientSideFilters) {
        return this.orderedConfigs.flatMap(config => {
          if (Number(config.is_pdp)) {
            const entry = this.kpiSummary[String(config.id)];

            if (!entry) {
              return [];
            }

            const percent = entry.count > 0 ? Math.round(entry.sum / entry.count) : 0;

            return [{
              id: config.id,
              name: config.display_name,
              percent,
              needCount: entry.needCount,
              total: entry.total,
              validCount: entry.total,
              threshold: SCORE_PASS_THRESHOLD,
            }];
          }

          const customFields = this.getFlatConfigCustomFields(config);

          if (customFields.length) {
            return customFields.map(field => {
              const cardId = `${config.id}:${field.key}`;
              const entry = this.kpiSummary[cardId];

              if (!entry) {
                return {
                  id: cardId,
                  name: field.label,
                  percent: 0,
                  needCount: 0,
                  total: 0,
                  validCount: 0,
                  threshold: SCORE_PASS_THRESHOLD,
                };
              }

              const percent = entry.count > 0 ? Math.round(entry.sum / entry.count) : 0;

              return {
                id: cardId,
                name: field.label,
                percent,
                needCount: entry.needCount,
                total: entry.total,
                validCount: entry.total,
                threshold: SCORE_PASS_THRESHOLD,
              };
            });
          }

          const entry = this.kpiSummary[String(config.id)];

          if (!entry) {
            return [];
          }

          const percent = entry.count > 0 ? Math.round(entry.sum / entry.count) : 0;

          return [{
            id: config.id,
            name: config.display_name,
            percent,
            needCount: entry.needCount,
            total: entry.total,
            validCount: entry.total,
            threshold: SCORE_PASS_THRESHOLD,
          }];
        });
      }

      const buildCard = ({ id, name, values }) => {
        // Only products with an available report/KPI value participate
        // in both the average and the X/Y denominator.
        const validValues = values.filter(value => value !== null);
        const total = validValues.length;

        const needCount = validValues.filter(
          value => value < SCORE_PASS_THRESHOLD
        ).length;

        const percent = total
          ? Math.round(
            validValues.reduce((sum, value) => sum + value, 0) / total
          )
          : 0;

        return {
          id,
          name,
          percent,
          needCount,
          total,
          validCount: total,
          threshold: SCORE_PASS_THRESHOLD,
        };
      };

      return this.orderedConfigs.flatMap(config => {
        if (Number(config.is_pdp)) {
          const values = products.map(product => {
            const metric = product.metrics[config.id];

            if (!metric) {
              return null;
            }

            const textValue = this.normalizeKpiMetricValue(metric.textNumeric);
            const imagesValue = this.normalizeKpiMetricValue(metric.imagesNumeric);

            const availableValues = [textValue, imagesValue]
              .filter(value => value !== null);

            if (!availableValues.length) {
              return null;
            }

            return availableValues.reduce((sum, value) => sum + value, 0) /
              availableValues.length;
          });

          return [buildCard({
            id: config.id,
            name: config.display_name,
            values,
          })];
        }

        const customFields = this.getFlatConfigCustomFields(config);

        if (customFields.length) {
          return customFields.map(field => {
            const values = products.map(product => {
              const metric = product.metrics[config.id];

              if (!metric || !Array.isArray(metric.customFields)) {
                return null;
              }

              const customField = metric.customFields.find(
                item => item.key === field.key
              );

              if (!customField) {
                return null;
              }

              return this.normalizeKpiMetricValue(
                customField.rawValue ??
                customField.value ??
                customField.numericValue
              );
            });

            return buildCard({
              id: `${config.id}:${field.key}`,
              name: field.label,
              values,
            });
          });
        }

        const values = products.map(product => {
          const metric = product.metrics[config.id];

          if (!metric) {
            return null;
          }

          return this.normalizeKpiMetricValue(metric.afterNumeric);
        });

        return [buildCard({
          id: config.id,
          name: config.display_name,
          values,
        })];
      });
    },

    kpiTotalNeedCount() {
      return this.kpiCards.reduce((sum, c) => sum + c.needCount, 0);
    },

    kpiCardLabels() {
      const raw = this.kpiCards.map(card => this.kpiCardLabel(card.name));
      const counts = raw.reduce((acc, label) => {
        acc[label] = (acc[label] || 0) + 1;
        return acc;
      }, {});
      const seen = {};
      return raw.map((label) => {
        if (counts[label] > 1) {
          seen[label] = (seen[label] || 0) + 1;
          return seen[label] === 1 ? label : `${label}${seen[label]}`;
        }
        return label;
      });
    },

    checkedSet() {
      return new Set(this.checkedProductIds);
    },

    allChecked() {
      return this.filteredProducts.length > 0 &&
        this.filteredProducts.every(p => this.checkedSet.has(p.rowKey));
    },

    activeFilterCount() {
      let count = [this.selectedRetailer, this.selectedCategory, this.selectedBrand].filter(Boolean).length;
      if (this.selectedDashboardConfig && this.selectedDashboardConfig !== 'all') count += 1;
      // Every checked item in the Product Lists block counts as one filter: each selected
      // list, plus "Individual items" when it is on. Nothing checked means no filter.
      const selectedLists = Array.isArray(this.selectedProductList) ? this.selectedProductList : [];
      count += selectedLists.length;
      if (this.showIndividualItems) count += 1;
      return count;
    },

    optimizeCatalogProductFilterOptions() {
      if (this.checkedProductIds.length > 0) {
        return [
          { value: 'selected', label: `Selected products (${this.checkedProductIds.length})` },
          ...this.optimizeCatalogProductFilters,
        ];
      }
      return this.optimizeCatalogProductFilters;
    },

    trackedCount() {
      if (!this.search.trim() && this.totalTrackedProducts) return this.totalTrackedProducts;
      return this.productsPagination.total || this.filteredProducts.length;
    },

    hasMoreProducts() {
      return Number(this.productsPagination.current_page || 1) < Number(this.productsPagination.last_page || 1);
    },

    formattedAfterLabel() {
      if (this.afterType === 'Custom date...') {
        return this.afterCustomDate || 'Custom date...';
      }

      return this.afterType;
    },

    optimizeCatalogSelectedIssues() {
      return this.optimizeCatalogIssues.filter((issue) => issue.selected);
    },

    sendEmailConfigOptions() {
      return this.orderedConfigs.map(config => ({
        value: config.id,
        label: config.display_name,
        config
      }));
    },

    orderedConfigs() {
      let configs = Array.isArray(this.dashboardConfigs) ? [...this.dashboardConfigs] : [...this.configs];
      if (this.selectedDashboardConfig) {
        if (this.selectedDashboardConfig !== 'all') {
          configs = configs.filter(config => Number(config.id) === Number(this.selectedDashboardConfig));
        }
        return configs.sort((a, b) => (a.order || 0) - (b.order || 0));
      }
      if (Array.isArray(this.selectedProductList) && this.selectedProductList.length > 0) {
        const selected = configs.filter(config => !config.product_id_lists?.length
          || config.product_id_lists.some(list => this.selectedProductList.includes(Number(list.id))));
        if (selected.length) configs = selected;
      }
      return configs.sort((a, b) => (a.order || 0) - (b.order || 0));
    },

    normalizedProducts() {
      return this.ajaxProducts.map((product, index) => {
        const metrics = {};

        this.orderedConfigs.forEach(config => {
          const isPdp = Number(config.is_pdp) === 1;

          const customFieldsConfig = Array.isArray(product[`${config.id}_custom_fields_config`])
            ? product[`${config.id}_custom_fields_config`]
            : [];

          const customFieldsValues = product[`${config.id}_custom_fields`] && typeof product[`${config.id}_custom_fields`] === 'object'
            ? product[`${config.id}_custom_fields`]
            : {};

          const beforeKey = `${config.id}_before_percent`;
          const beforeRaw = product[beforeKey] ?? '0%';
          const beforeNumeric = this.normalizePercent(beforeRaw);

          if (isPdp) {
            const textKey = `${config.id}_text_percent`;
            const imagesKey = `${config.id}_images_percent`;

            const textRaw = product[textKey] ?? '0%';
            const imagesRaw = product[imagesKey] ?? '0%';

            const textNumeric = product[`${config.id}_text_score_available`]
              ? this.normalizePercent(textRaw)
              : null;
            const imagesNumeric = product[`${config.id}_images_score_available`]
              ? this.normalizePercent(imagesRaw)
              : null;

            const availableScores = [textNumeric, imagesNumeric].filter(v => v !== null);
            const avgScore = availableScores.length ? availableScores.reduce((a, b) => a + b, 0) / availableScores.length : null;

            metrics[config.id] = {
              before: this.formatPercent(beforeRaw),
              beforeNumeric,

              // No report for this product and prompt: show a dash, never a made-up 0%.
              text: textNumeric === null ? '—' : this.formatPercent(textRaw),
              textNumeric,

              images: imagesNumeric === null ? '—' : this.formatPercent(imagesRaw),
              imagesNumeric,

              delta: (availableScores.length === 2 && product[`${config.id}_delta`] != null)
                ? Number(product[`${config.id}_delta`])
                : (avgScore !== null && beforeNumeric !== null ? avgScore - beforeNumeric : null),

              beforeColor: product[`${config.id}_before_color`] ?? null,
              textColor: product[`${config.id}_text_color`] ?? null,
              imagesColor: product[`${config.id}_images_color`] ?? null,

              beforeReportUrl: product[`${config.id}_before_report_url`] ?? null,
              beforeReportId: product[`${config.id}_before_report_id`] ?? null,
              beforeReportUuid: product[`${config.id}_before_report_uuid`] ?? null,

              textReportUrl: product[`${config.id}_text_report_url`] ?? null,
              textReportId: product[`${config.id}_text_report_id`] ?? null,
              textReportUuid: product[`${config.id}_text_report_uuid`] ?? null,

              imagesReportUrl: product[`${config.id}_images_report_url`] ?? null,
              imagesReportId: product[`${config.id}_images_report_id`] ?? null,
              imagesReportUuid: product[`${config.id}_images_report_uuid`] ?? null,
            };

            return;
          }

          const flatCustomFieldsConfig = customFieldsConfig.flatMap(field => {
            if (Array.isArray(field.children) && field.children.length) {
              return field.children;
            }

            return [field];
          });

          const customFields = flatCustomFieldsConfig.map(field => {
            const rawValue = customFieldsValues[field.key] ?? null;
            const numericValue = this.normalizeMetricValue(rawValue);

            return {
              label: field.label,
              key: field.key,
              rawValue,
              value: this.formatCustomFieldValue(rawValue, field),
              numericValue,
            };
          });

          const afterKey = `${config.id}_percent`;
          const afterRaw = product[afterKey] ?? '0%';
          const afterNumeric = product[`${config.id}_score_available`]
            ? this.normalizePercent(afterRaw)
            : null;

          metrics[config.id] = {
            before: this.formatPercent(beforeRaw),
            beforeNumeric,
            // No report for this product and prompt: show a dash, never a made-up 0%.
            after: afterNumeric === null ? '—' : this.formatPercent(afterRaw),
            afterNumeric,
            delta: product[`${config.id}_delta`] != null
              ? Number(product[`${config.id}_delta`])
              : (afterNumeric !== null && beforeNumeric !== null ? afterNumeric - beforeNumeric : null),
            color: product[`${config.id}_color`] ?? null,
            beforeColor: product[`${config.id}_before_color`] ?? null,
            beforeReportUrl: product[`${config.id}_before_report_url`] ?? null,
            reportUrl: product[`${config.id}_report_url`] ?? null,
            reportId: product[`${config.id}_report_id`] ?? null,
            beforeReportId: product[`${config.id}_before_report_id`] ?? null,
            reportUuid: product[`${config.id}_report_uuid`] ?? null,
            beforeReportUuid: product[`${config.id}_before_report_uuid`] ?? null,
            customFields,
          };
        });

        return {
          ...product,
          rowKey: product.row_key ?? product.product_id_list_record_id ?? product.product_id ?? index,
          metrics,
        };
      });
    },

    filteredProducts() {
      let products = this.normalizedProducts;

      if (this.selectedRetailer) {
        products = products.filter(item => {
          return this.getRetailerFromProduct(item) === this.selectedRetailer;
        });
      }

      if (this.selectedBrand) {
        products = products.filter(item => {
          return String(item.product_brand || '').trim() === this.selectedBrand;
        });
      }

      if (this.selectedCategory) {
        products = products.filter(item => {
          return String(item.product_category || item.category || '').trim() === this.selectedCategory;
        });
      }

      if (this.sortKey) {
        const direction = this.sortDir === 'asc' ? 1 : -1;

        products = [...products].sort((a, b) => {
          const priority = (a.list_priority ?? 1) - (b.list_priority ?? 1);
          if (priority) return priority;
          const aValue = this.getSortValue(a, this.sortKey);
          const bValue = this.getSortValue(b, this.sortKey);

          if (typeof aValue === 'string' || typeof bValue === 'string') {
            return direction * String(aValue).localeCompare(String(bValue));
          }

          return direction * ((aValue ?? 0) - (bValue ?? 0));
        });
      }

      return products;
    },

    productUrl() {
      return this.buildProductUrl(1);
    },
  },
  watch: {
    configs(value) {
      this.dashboardConfigs = Array.isArray(value) ? [...value] : [];
    },
    showIndividualItems(value) {
      window.optixShowIndividualItems = value;
      this.getProducts();
    },
    selectedDashboardConfig(value) {
      window.optixDashboardConfigSelection = value;
      this.sortKey = null;
      this.checkedProductIds = [];
      this.ajaxProducts = [];
      this.hasLoadedProducts = false;
      this.getProducts();
    },
    search() {
      this._debouncedGetProducts();
    },

    beforeRange() {
      this.getProducts();
    },

    afterType() {
      this.getProducts();
    },

    beforeCustomDate() {
      if (this.beforeRange === 'custom') {
        this.getProducts();
      }
    },

    afterCustomDate() {
      if (this.afterType === 'Custom date...') {
        this.getProducts();
      }
    },
    selectedBrand() {
      this.closeFiltersPopover();
    },
    selectedCategory() {
      this.closeFiltersPopover();
    },
    selectedRetailer() {
      this.closeFiltersPopover();
    },
    compareMode() {
      this.getProducts();
    },

    showEmptyState(value) {
      if (!value && this.openFiltersMenu === 'empty-state') {
        this.openFiltersMenu = null;
      }
    },

    filteredProducts(newVal) {
      const visibleKeys = new Set(newVal.map(p => p.rowKey));
      this.checkedProductIds = this.checkedProductIds.filter(id => visibleKeys.has(id));
      if (this.checkedProductIds.length === 0 && this.optimizeCatalogProductFilter === 'selected') {
        this.optimizeCatalogProductFilter = 'all';
      }
    },
  },

  created() {
    window.optixShowIndividualItems = this.showIndividualItems;
    window.optixDashboardConfigSelection = this.selectedDashboardConfig;
    // Non-reactive — must not go in data(); Vue 2 does not proxy _ or $-prefixed keys
    this.searchAbortController = null;
    this.kpiAbortController = null;
    this.requestGeneration = 0;

    this._debouncedGetProducts = debounce(() => {
      const searchStr = this.search.trim();

      const rejectInput = (message) => {
        this.searchError = message;
        if (this.searchAbortController) this.searchAbortController.abort();
        if (this.kpiAbortController) this.kpiAbortController.abort();
        this.requestGeneration += 1;
        // Also invalidate any in-flight KPI response. Since search is removed from
        // hasClientSideFilters, a stale kpiSummary for the previous valid search
        // would otherwise be displayed against the current (unchanged) table rows.
        this.kpiSummaryToken++;
        this.kpiSummary = null;
        // The aborted product request's finally block fails the generation check
        // and cannot clear these.
        this.isLoadingProducts = false;
        this.isLoadingMoreProducts = false;
      };

      if (searchStr.length > 500) {
        rejectInput('Search query is too long. Please shorten it.');
        return;
      }

      const terms = [...new Set(searchStr.split(/[\s,]+/).filter(Boolean))];
      if (terms.length > 50) {
        rejectInput('Too many search terms. Please use 50 or fewer.');
        return;
      }

      this.searchError = null;
      this.getProducts();
    }, 400);

    // window.Echo.channel(`report_score.${this.companyId}`)
    //   .listen('.report.score.completed', () => {
    //     this.getProducts();
    //   });

    if (window.optixProductListsError) {
      this.allProductListsLoadError = true;
      this.selectedProductList = Array.isArray(this.selectedProductList)
        ? this.selectedProductList
        : [];
    } else if (window.optixProductListsLoaded) {
      this.allProductLists = window.optixProductLists || [];
      this.allProductListsLoaded = true;
      this.selectedProductList = Array.isArray(window.optixSelectedProductLists)
        ? window.optixSelectedProductLists.map(Number)
        : this.allProductLists.map(list => Number(list.id));
    } else {
      window.addEventListener('product-lists-loaded', (e) => {
        this.allProductLists = e.detail;
        this.allProductListsLoaded = true;
      });
      window.addEventListener('product-lists-load-error', () => {
        this.allProductListsLoadError = true;
        if (!Array.isArray(this.selectedProductList)) {
          this.selectedProductList = [];
          this.getProducts();
        }
      });
    }

    window.addEventListener('product-list-changed', (e) => {
      const hadSearch = this.search !== '';
      const selectedLists = e.detail.map(Number);
      const isSubsetSelection = selectedLists.length > 0
        && this.allProductLists.length > 0
        && selectedLists.length < this.allProductLists.length;
      // "All" and "Clear" never move the Individual items toggle on the user's behalf.
      const keepIndividualItems = this.keepIndividualItems;
      const shouldHideIndividualItems = !keepIndividualItems && isSubsetSelection && this.showIndividualItems;
      const shouldShowIndividualItems = !keepIndividualItems && !isSubsetSelection && !this.showIndividualItems;

      this.selectedProductList = selectedLists;
      this.search = '';
      this.searchError = null;

      if (shouldHideIndividualItems) {
        this.showIndividualItems = false;
        return;
      }

      if (shouldShowIndividualItems) {
        this.showIndividualItems = true;
        return;
      }

      if (!hadSearch) {
        this.getProducts();
      }
    });

    window.addEventListener('send-report-modal:open', () => {
      this.openSendToEmailModal();
    })
  },

  mounted() {
    if (Array.isArray(this.selectedProductList) || this.allProductListsLoadError) {
      this.getProducts();
    }

    this._handleDashboardConfigsRefresh = (event) => {
      this.refreshDashboardConfigs(event.detail || {});
    };

    window.addEventListener('optix:dashboard-configs-refresh', this._handleDashboardConfigsRefresh);

    this._handleBackToDashboard = () => {
      this.closeReportFrame(!this._didPushReportHistory);
    };

    window.addEventListener('optix:back-to-dashboard', this._handleBackToDashboard);

    this._handlePopState = () => {
      if (!this.activeReportUrl || !this._didPushReportHistory) {
        return;
      }
      const url = new URL(window.location.href);
      if (!url.searchParams.get('report')) {
        this.closeReportFrame(true);
      }
    };

    window.addEventListener('popstate', this._handlePopState);

    this._stickyTable = createStickyTableScroll({
      getWrap: () => this.$refs.tableWrap,
      getRoot: () => this.$el,
    });

    this.$nextTick(() => {
      if (typeof window.lucide !== 'undefined') {
        window.lucide.createIcons();
      }
      this._stickyTable.update();
      this.initKpiTooltips();
    });
  },

  updated() {
    this.$nextTick(() => {
      if (typeof window.lucide !== 'undefined') {
        window.lucide.createIcons();
      }
      if (this._stickyTable) {
        this._stickyTable.update();
      }
      this.initKpiTooltips();
    });
  },

  beforeDestroy() {
    this.destroyKpiTooltips();

    if (this._handleBackToDashboard) {
      window.removeEventListener('optix:back-to-dashboard', this._handleBackToDashboard);
    }
    if (this._handlePopState) {
      window.removeEventListener('popstate', this._handlePopState);
    }
    if (this._handleDashboardConfigsRefresh) {
      window.removeEventListener('optix:dashboard-configs-refresh', this._handleDashboardConfigsRefresh);
    }
    if (this._stickyTable) {
      this._stickyTable.destroy();
    }
    if (this._debouncedGetProducts) {
      this._debouncedGetProducts.cancel();
    }
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }
    if (this.kpiAbortController) {
      this.kpiAbortController.abort();
    }
  },

  methods: {
    async refreshDashboardConfigs(eventDetail = {}) {
      try {
        const { data } = await axios.get('/analytics-history/optix-dashboard-configs');
        const configs = Array.isArray(data?.configs) ? data.configs : [];
        const configId = eventDetail?.configId || eventDetail?.config_id || null;

        this.dashboardConfigs = configs;
        this.sortKey = null;
        this.checkedProductIds = [];
        this.ajaxProducts = [];
        this.hasLoadedProducts = false;

        if (configId && this.selectedDashboardConfig && this.selectedDashboardConfig !== 'all') {
          const hasSelectedConfig = configs.some(config => Number(config.id) === Number(this.selectedDashboardConfig));

          if (!hasSelectedConfig) {
            this.selectedDashboardConfig = String(configId);
            return;
          }
        }

        this.getProducts();
      } catch (error) {
        console.error('Failed to refresh dashboard configurations:', error);
        this.getProducts();
      }
    },

    showImageZoom(event, item) {
      if (!item?.image) {
        return;
      }

      this.imageZoom.visible = true;
      this.imageZoom.src = item.image;
      this.imageZoom.alt = item.product_name || 'Product image';

      this.moveImageZoom(event);
    },

    moveImageZoom(event) {
      const previewWidth = 280;
      const previewHeight = 320;
      const gap = 16;

      let x = event.clientX + gap;
      let y = event.clientY + gap;

      if (x + previewWidth > window.innerWidth) {
        x = event.clientX - previewWidth - gap;
      }

      if (y + previewHeight > window.innerHeight) {
        y = window.innerHeight - previewHeight - gap;
      }

      if (y < gap) {
        y = gap;
      }

      this.imageZoom.x = x;
      this.imageZoom.y = y;
    },

    hideImageZoom() {
      this.imageZoom.visible = false;
      this.imageZoom.src = '';
      this.imageZoom.alt = '';
    },
    getProductIdLabel(product) {
      if (product?.is_asin) {
        return 'ASIN';
      }

      if (product?.is_tcin) {
        return 'TCIN';
      }

      if (product?.is_gtin) {
        return 'GTIN';
      }

      return 'ID';
    },
    getProductBrandLine(product) {
      const parts = [];

      if (product?.product_brand) {
        parts.push(product.product_brand);
      }

      if (product?.product_id) {
        parts.push(`${this.getProductIdLabel(product)}: ${product.product_id}`);
      }

      const retailer = this.getRetailerFromProduct(product);

      if (retailer) {
        parts.push(retailer);
      }

      return parts.join(' • ');
    },
    getRetailerFromProduct(product) {
      if (product?.is_asin) {
        return 'Amazon';
      }

      if (product?.is_walmart) {
        return 'Walmart';
      }

      if (product?.is_tcin) {
        return 'Target';
      }

      return this.getRetailerFromUrl(
        product?.url
        || product?.product_url
        || product?.item_url
      );
    },
    getRetailerFromUrl(value) {
      if (!value) {
        return null;
      }

      try {
        const url = new URL(value, window.location.origin);
        const hostname = url.hostname.replace(/^www\./, '').toLowerCase();

        const knownRetailers = {
          'amazon.com': 'Amazon',
          'walmart.com': 'Walmart',
          'homedepot.com': 'Home Depot',
          'lowes.com': 'Lowe’s',
          'target.com': 'Target',
          'bestbuy.com': 'Best Buy',
          'costco.com': 'Costco',
          'staples.com': 'Staples',
          'officedepot.com': 'Office Depot',
        };

        const matchedDomain = Object.keys(knownRetailers).find((domain) => {
          return hostname === domain || hostname.endsWith(`.${domain}`);
        });

        if (matchedDomain) {
          return knownRetailers[matchedDomain];
        }

        return hostname
          .split('.')[0]
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, (char) => char.toUpperCase());
      } catch (error) {
        return null;
      }
    },
    toggleFiltersPopover(source) {
      this.openFiltersMenu = this.openFiltersMenu === source ? null : source;
    },

    // Only close this trigger's own menu — the other trigger's outside-click would cancel its open.
    closeFiltersPopover(source) {
      if (!source || this.openFiltersMenu === source) {
        this.openFiltersMenu = null;
      }
    },

    toggleProductListItem(id) {
      const current = Array.isArray(this.selectedProductList) ? [...this.selectedProductList] : [];
      const idx = current.indexOf(id);
      if (idx > -1) {
        current.splice(idx, 1);
      } else {
        current.push(id);
      }
      window.setProductListSelection(current);
    },

    // "All" and "Clear" act on the ordinary lists only. The curated Dashboard list keeps
    // whatever the user ticked, and the Individual items toggle is left untouched.
    selectAllProductListsInPopover() {
      this.withPreservedIndividualItems(() => {
        window.setProductListSelection([
          ...this.selectedDashboardListIds(),
          ...this.plainProductListIds(),
        ]);
      });
    },

    clearAllProductListsInPopover() {
      this.withPreservedIndividualItems(() => {
        window.setProductListSelection(this.selectedDashboardListIds());
      });
    },

    withPreservedIndividualItems(apply) {
      this.keepIndividualItems = true;

      try {
        // setProductListSelection dispatches product-list-changed synchronously.
        apply();
      } finally {
        this.keepIndividualItems = false;
      }
    },

    plainProductListIds() {
      return this.allProductLists
        .filter(list => !this.isDashboardProductList(list))
        .map(list => Number(list.id));
    },

    selectedDashboardListIds() {
      const selected = Array.isArray(this.selectedProductList)
        ? this.selectedProductList.map(Number)
        : [];

      return this.allProductLists
        .filter(list => this.isDashboardProductList(list))
        .map(list => Number(list.id))
        .filter(id => selected.indexOf(id) > -1);
    },

    isCanceledRequest(error) {
      return error?.code === 'ERR_CANCELED'
        || error?.name === 'CanceledError'
        || axios.isCancel?.(error);
    },

    normalizeDate(date) {
      if (!date) {
        return null;
      }

      return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );
    },

    isDateBefore(firstDate, secondDate) {
      const first = this.normalizeDate(firstDate);
      const second = this.normalizeDate(secondDate);

      if (!first || !second) {
        return false;
      }

      return first.getTime() < second.getTime();
    },

    isDateAfter(firstDate, secondDate) {
      const first = this.normalizeDate(firstDate);
      const second = this.normalizeDate(secondDate);

      if (!first || !second) {
        return false;
      }

      return first.getTime() > second.getTime();
    },

    isDateDisabledForActivePicker(date) {
      if (this.activeDatePicker === 'before') {
        const afterDate = this.parseDate(this.afterCustomDate);

        if (!afterDate) {
          return false;
        }

        return this.isDateAfter(date, afterDate);
      }

      if (this.activeDatePicker === 'after') {
        const beforeDate = this.parseDate(this.beforeCustomDate);

        if (!beforeDate) {
          return false;
        }

        return this.isDateBefore(date, beforeDate);
      }

      return false;
    },
    syncCustomDateRange(changedType) {
      const beforeDate = this.parseDate(this.beforeCustomDate);
      const afterDate = this.parseDate(this.afterCustomDate);

      if (!beforeDate || !afterDate) {
        return;
      }

      if (!this.isDateAfter(beforeDate, afterDate)) {
        return;
      }

      if (changedType === 'before') {
        this.afterCustomDate = '';
        return;
      }

      if (changedType === 'after') {
        this.beforeCustomDate = '';
      }
    },
    openDatePicker(type) {
      this.activeDatePicker = type;

      const selectedDate = this.parseDate(this.getDatePickerValue(type));

      if (selectedDate) {
        this.calendarViewDate = new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          1
        );

        return;
      }

      this.calendarViewDate = new Date();
    },

    closeDatePicker(type) {
      if (this.activeDatePicker === type) {
        this.activeDatePicker = null;
      }
    },

    getDatePickerValue(type) {
      if (type === 'before') {
        return this.beforeCustomDate;
      }

      if (type === 'after') {
        return this.afterCustomDate;
      }

      return '';
    },

    setDatePickerValue(type, value) {
      if (type === 'before') {
        this.beforeCustomDate = value;
        return;
      }

      if (type === 'after') {
        this.afterCustomDate = value;
      }
    },

    clearDatePickerValue(type) {
      this.setDatePickerValue(type, '');

      if (this.activeDatePicker === type) {
        this.activeDatePicker = null;
      }

      this.getProducts();
    },

    selectDatePickerValue(date) {
      if (!this.activeDatePicker) {
        return;
      }

      if (this.isDateDisabledForActivePicker(date)) {
        return;
      }

      const changedType = this.activeDatePicker;

      this.setDatePickerValue(changedType, this.formatDate(date));
      this.syncCustomDateRange(changedType);

      this.activeDatePicker = null;
      this.getProducts();
    },

    goToPreviousMonth() {
      this.calendarViewDate = new Date(
        this.calendarViewDate.getFullYear(),
        this.calendarViewDate.getMonth() - 1,
        1
      );
    },

    goToNextMonth() {
      this.calendarViewDate = new Date(
        this.calendarViewDate.getFullYear(),
        this.calendarViewDate.getMonth() + 1,
        1
      );
    },

    formatDate(date) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },

    parseDate(value) {
      if (!value) {
        return null;
      }

      const date = new Date(value);

      if (Number.isNaN(date.getTime())) {
        return null;
      }

      return date;
    },

    isSameDate(firstDate, secondDate) {
      return (
        firstDate.getFullYear() === secondDate.getFullYear()
        && firstDate.getMonth() === secondDate.getMonth()
        && firstDate.getDate() === secondDate.getDate()
      );
    },
    parseRequirementValue(value) {
      const raw = String(value ?? '').trim();

      if (!raw || raw === '—') {
        return null;
      }

      const rangeMatch = raw.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)$/);

      if (rangeMatch) {
        return {
          type: 'range',
          actual: Number(rangeMatch[1]),
          min: Number(rangeMatch[2]),
          max: Number(rangeMatch[3]),
        };
      }

      const targetMatch = raw.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);

      if (targetMatch) {
        return {
          type: 'target',
          actual: Number(targetMatch[1]),
          target: Number(targetMatch[2]),
        };
      }

      return null;
    },

    requirementLevelClass(value) {
      const parsed = this.parseRequirementValue(value);

      if (!parsed) {
        return null;
      }

      if (parsed.type === 'range') {
        if (parsed.actual >= parsed.min && parsed.actual <= parsed.max) {
          return 'score-pill-green';
        }

        return 'score-pill-red';
      }

      if (parsed.type === 'target') {
        if (parsed.actual >= parsed.target) {
          return 'score-pill-green';
        }

        return 'score-pill-red';
      }

      return null;
    },

    formatRequirementValue(value) {
      const parsed = this.parseRequirementValue(value);

      if (!parsed) {
        return null;
      }

      return String(parsed.actual);
    },

    isPlainNumberCustomField(field) {
      const fieldKey = String(field?.key || '').toLowerCase();
      const fieldLabel = String(field?.label || '').toLowerCase();

      return [
        'images',
        'rating',
        'reviews',
        'review',
        'review_count',
        'reviews_count',
      ].some((token) => fieldKey === token || fieldLabel === token);
    },

    formatCustomFieldNumber(value) {
      const numeric = Number(value);

      if (!Number.isFinite(numeric)) {
        return '—';
      }

      return Number.isInteger(numeric)
        ? String(numeric)
        : String(numeric);
    },

    formatCustomFieldValue(value, field = null) {
      const normalized = String(value ?? '').trim().toLowerCase();

      if (normalized === 'ok') {
        return 'OK';
      }

      if (normalized === 'fix') {
        return 'Fix';
      }

      if (normalized === 'yes') {
        return 'Yes';
      }

      if (normalized === 'no') {
        return 'No';
      }

      if (normalized === 'missing') {
        return 'X';
      }

      const requirementValue = this.formatRequirementValue(value);

      if (requirementValue) {
        return requirementValue;
      }

      const numericValue = this.normalizeMetricValue(value);

      if (numericValue !== null) {
        const fieldKey = String(field?.key || '').toLowerCase();

        if (fieldKey.includes('listing') || this.isPlainNumberCustomField(field)) {
          return this.formatCustomFieldNumber(value);
        }

        return `${numericValue}%`;
      }

      return value || '—';
    },

    customFieldPillClass(field) {
      const normalized = String(field.rawValue ?? '').trim().toLowerCase();

      if (['ok', 'yes'].includes(normalized)) {
        return 'score-pill-green';
      }

      if (['fix', 'no', 'missing'].includes(normalized)) {
        return 'score-pill-red';
      }

      const requirementClass = this.requirementLevelClass(field.rawValue);

      if (requirementClass) {
        return requirementClass;
      }

      if (field.numericValue !== null) {
        if (this.isPlainNumberCustomField(field)) {
          return 'score-pill-light';
        }

        return this.scoreLevelClass(field.numericValue);
      }

      return 'score-pill-light';
    },
    getRawConfigCustomFields(config) {
      const firstProductWithConfig = this.filteredProducts.find(product => {
        return Array.isArray(product[`${config.id}_custom_fields_config`])
          && product[`${config.id}_custom_fields_config`].length;
      });

      return firstProductWithConfig?.[`${config.id}_custom_fields_config`] || [];
    },

    getConfigCustomFieldGroups(config) {
      return this.getRawConfigCustomFields(config)
        .filter(field => Array.isArray(field.children) && field.children.length)
        .map(field => ({
          label: field.label || '',
          key: field.key || this.normalizeCustomFieldKeyForUi(field.label || 'group'),
          children: field.children
            .filter(child => child && child.key)
            .map(child => ({
              label: child.label || child.key,
              key: child.key,
            })),
        }))
        .filter(group => group.label && group.children.length);
    },

    getFlatConfigCustomFields(config) {
      return this.getRawConfigCustomFields(config)
        .flatMap(field => {
          if (Array.isArray(field.children) && field.children.length) {
            return field.children;
          }

          return [field];
        })
        .filter(field => field && field.key)
        .map(field => ({
          label: field.label || field.key,
          key: field.key,
        }));
    },

    getConfigCustomFields(config) {
      return this.getFlatConfigCustomFields(config);
    },

    hasGroupedCustomFields(config) {
      return this.getConfigCustomFieldGroups(config).length > 0;
    },

    normalizeCustomFieldKeyForUi(value) {
      return String(value || '')
        .trim()
        .toLowerCase()
        .replace(/[-/\\]+/g, '_')
        .replace(/[^\w\s]+/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
    },

    getConfigColspan(config) {
      if (Number(config.is_pdp) === 1) {
        return this.compareMode ? 4 : 2;
      }

      const customFieldsCount = this.getFlatConfigCustomFields(config).length;

      if (customFieldsCount) {
        return customFieldsCount + (this.compareMode ? 2 : 0);
      }

      return this.compareMode ? 3 : 1;
    },
    isStatusValue(value) {
      const normalized = String(value || '').trim().toLowerCase();

      return ['ok', 'missing'].includes(normalized);
    },
    shouldPassOptixDebug() {
      const currentUrl = new URL(window.location.href);

      return currentUrl.searchParams.has('debug_optix')
        || currentUrl.searchParams.has('debug_dashboard')
        || currentUrl.searchParams.has('optix_debug');
    },

    isDashboardProductList(list) {
      return String(list && list.name ? list.name : '').trim().toLowerCase() === 'dashboard';
    },

    getSearchTerms() {
      return [...new Set(this.search.split(/[\s,]+/).filter(Boolean))];
    },

    buildKpiSummaryUrl() {
      const url = new URL('/analytics-history/optix-kpi-summary', window.location.origin);

      url.searchParams.set('after_type', this.afterType);

      if (this.afterType === 'Custom date...' && this.afterCustomDate) {
        url.searchParams.set('after_date', this.afterCustomDate);
      }

      if (Array.isArray(this.selectedProductList)) {
        if (this.selectedProductList.length) {
          this.selectedProductList.forEach(id => url.searchParams.append('product_list_ids[]', id));
        } else {
          url.searchParams.set('product_list_ids', '');
        }
      }

      if (this.search.trim()) {
        url.searchParams.set('search', this.getSearchTerms().join(','));
      }

      if (this.selectedDashboardConfig) {
        url.searchParams.set('dashboard_config_id', this.selectedDashboardConfig);
      }
      url.searchParams.set('include_individual_items', this.showIndividualItems === false ? '0' : '1');
      return url.toString();
    },

    resizeSearchInput() {
      const el = this.$refs.searchInput;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    },

    clearSearch() {
      this.search = '';
      this.searchError = null;
      this.$nextTick(() => this.resizeSearchInput());
    },

    async fetchKpiSummary() {
      this.kpiSummary = null;
      this.kpiSummaryToken++;
      const token = this.kpiSummaryToken;

      if (this.kpiAbortController) {
        this.kpiAbortController.abort();
      }
      this.kpiAbortController = new AbortController();

      try {
        const { data } = await axios.get(this.buildKpiSummaryUrl(), {
          signal: this.kpiAbortController.signal,
        });

        if (token !== this.kpiSummaryToken) {
          return;
        }

        this.kpiSummary = data.kpi || null;
      } catch (error) {
        if (this.isCanceledRequest(error)) {
          return;
        }
        if (token === this.kpiSummaryToken) {
          this.kpiSummary = null;
        }
      } finally {
        if (token === this.kpiSummaryToken) {
          this.kpiAbortController = null;
        }
      }
    },

    buildProductUrl(page = 1) {
      const url = new URL(this.url, window.location.origin);

      url.searchParams.set('before', this.beforeRange);
      url.searchParams.set('after_type', this.afterType);
      url.searchParams.set('page', page);
      url.searchParams.set('per_page', this.productsPagination.per_page || 100);

      if (this.compareMode) {
        url.searchParams.set('compare', '1');
        url.searchParams.set('before', this.beforeRange);

        if (this.beforeRange === 'custom' && this.beforeCustomDate) {
          url.searchParams.set('before_date', this.beforeCustomDate);
        }
      }

      if (this.afterType === 'Custom date...' && this.afterCustomDate) {
        url.searchParams.set('after_date', this.afterCustomDate);
      }

      if (Array.isArray(this.selectedProductList)) {
        if (this.selectedProductList.length) {
          this.selectedProductList.forEach(id => url.searchParams.append('product_list_ids[]', id));
        } else {
          url.searchParams.set('product_list_ids', '');
        }
      }

      if (this.shouldPassOptixDebug()) {
        url.searchParams.set('debug_optix', '1');
      }

      if (this.search.trim()) {
        url.searchParams.set('search', this.getSearchTerms().join(','));
      }

      if (this.selectedDashboardConfig) {
        url.searchParams.set('dashboard_config_id', this.selectedDashboardConfig);
      }
      url.searchParams.set('include_individual_items', this.showIndividualItems === false ? '0' : '1');
      return url.toString();
    },

    async getProducts(page = 1, append = false) {
      if (append && (this.isLoadingMoreProducts || !this.hasMoreProducts)) {
        return;
      }

      this.searchError = null;
      this.productsLoadError = false;

      this.requestGeneration += 1;
      const myGeneration = this.requestGeneration;

      if (append) {
        this.isLoadingMoreProducts = true;
      } else {
        if (this.searchAbortController) {
          this.searchAbortController.abort();
          this.isLoadingMoreProducts = false;
        }
        this.searchAbortController = new AbortController();
        this.isLoadingProducts = true;

        if (!this.hasClientSideFilters) {
          this.fetchKpiSummary();
        } else {
          if (this.kpiAbortController) this.kpiAbortController.abort();
          this.kpiSummary = null;
          this.kpiSummaryToken++;
        }
      }

      try {
        const { data } = await axios.get(this.buildProductUrl(page), {
          signal: this.searchAbortController?.signal,
        });

        if (this.requestGeneration !== myGeneration) return;

        const products = Array.isArray(data.data) ? data.data : [];
        this.ajaxProducts = append ? [...this.ajaxProducts, ...products] : products;
        this.productsPagination = {
          current_page: data.pagination?.current_page || page,
          last_page: data.pagination?.last_page || 1,
          per_page: data.pagination?.per_page || this.productsPagination.per_page || 100,
          total: data.pagination?.total || products.length,
        };

        if (!this.search.trim()) {
          this.totalTrackedProducts = this.productsPagination.total;
        }
      } catch (error) {
        if (this.isCanceledRequest(error)) return;
        if (this.requestGeneration !== myGeneration) return;

        const responseData = error.response?.data;
        const isSearchError = error.response?.status === 422
          && (responseData?.search_error || responseData?.errors?.search);

        if (isSearchError) {
          this.searchError = responseData.search_error
            || responseData.errors.search[0]
            || 'Search input is invalid.';
          return;
        }

        console.error('Failed to load products:', error);
        this.productsLoadError = true;

        if (!append) {
          this.ajaxProducts = [];
          this.productsPagination = {
            current_page: 1,
            last_page: 1,
            per_page: this.productsPagination.per_page || 100,
            total: 0,
          };
        }
      } finally {
        if (this.requestGeneration === myGeneration) {
          this.isLoadingProducts = false;
          this.isLoadingMoreProducts = false;
          this.hasLoadedProducts = true;
        }
      }
    },

    loadMoreProducts() {
      if (!this.hasMoreProducts || this.isLoadingMoreProducts) {
        return;
      }

      this.getProducts(Number(this.productsPagination.current_page || 1) + 1, true);
    },

    normalizePercent(value) {
      if (value === null || value === undefined || value === '') {
        return 0;
      }

      if (typeof value === 'number') {
        return Math.round(value);
      }

      const numeric = parseFloat(String(value).replace('%', '').trim());
      return Number.isNaN(numeric) ? 0 : Math.round(numeric);
    },

    formatPercent(value) {
      return `${this.normalizePercent(value)}%`;
    },

    normalizeMetricValue(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      if (typeof value === 'string' && value.includes('/')) {
        return null;
      }

      if (typeof value === 'number') {
        return Math.round(value);
      }

      const normalized = String(value).replace('%', '').trim();
      if (!normalized) {
        return null;
      }

      const numeric = parseFloat(normalized);
      return Number.isNaN(numeric) ? null : Math.round(numeric);
    },

    normalizeKpiMetricValue(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const normalized = String(value).trim().toLowerCase();

      if (['x', 'no'].includes(normalized)) {
        return 0;
      }

      if (['yes', '+'].includes(normalized)) {
        return 100;
      }

      const numericValue = this.normalizeMetricValue(value);

      if (numericValue === null) {
        return null;
      }

      return Math.max(0, Math.min(100, numericValue));
    },

    formatMetricValue(value) {
      if (value === null || value === undefined || value === '') {
        return '—';
      }

      if (typeof value === 'number') {
        return `${Math.round(value)}%`;
      }

      const normalized = String(value).trim();
      if (!normalized) {
        return '—';
      }

      const numeric = this.normalizeMetricValue(normalized);
      if (numeric !== null) {
        return `${numeric}%`;
      }

      return normalized;
    },

    abs(value) {
      return value == null ? '' : Math.abs(value);
    },

    overallScoreLevelClass(item) {
      const hasScore = Object.values(item.metrics).some(metric =>
        [metric.afterNumeric, metric.textNumeric, metric.imagesNumeric].some(value => value != null)
      );
      return this.scoreLevelClass(hasScore ? this.normalizePercent(item.optix_score) : null);
    },

    scoreLevelClass(score) {
      if (score == null || !Number.isFinite(Number(score))) return 'score-pill-gray';
      if (score >= SCORE_PASS_THRESHOLD) return 'score-pill-green';
      if (score >= SCORE_YELLOW_THRESHOLD) return 'score-pill-yellow';
      return 'score-pill-red';
    },

    kpiCardColorClass(percent) {
      if (percent >= SCORE_PASS_THRESHOLD) return 'kpi-card-green';
      if (percent >= SCORE_YELLOW_THRESHOLD) return 'kpi-card-amber';
      return 'kpi-card-red';
    },

    deltaClass(delta) {
      if (delta > 0) return 'delta-up';
      if (delta < 0) return 'delta-down';
      return 'delta-neutral';
    },

    deltaIcon(delta) {
      if (delta > 0) return 'bi bi-arrow-up';
      if (delta < 0) return 'bi bi-arrow-down';
      return 'bi bi-dash';
    },

    getConfigIconType(config) {
      const icon = config?.icon || '';

      if (
        icon === 'custom-ab-test' ||
        icon === 'custom-image-order' ||
        icon === 'custom-prioritize' ||
        icon === 'custom-holidays'
      ) {
        return 'image';
      }

      if (icon.startsWith('lucide-')) {
        return 'lucide';
      }

      return 'class';
    },

    getConfigIconValue(config) {
      const icon = config?.icon || '';

      switch (icon) {
        case 'custom-ab-test':
          return '/img/icons/ab-testing.svg';
        case 'custom-image-order':
          return '/img/icons/image-order.svg';
        case 'custom-prioritize':
          return '/img/icons/prioritize.svg';
        case 'custom-holidays':
          return '/img/icons/gift-box.svg';
        default:
          if (icon.startsWith('lucide-')) {
            return icon.replace('lucide-', '');
          }

          return icon;
      }
    },

    pdpNavStorageKey() {
      return `optix_pdp_nav_${this.companyId}`;
    },

    findClickedMetric(reportUrl) {
      const fields = ['reportUrl', 'textReportUrl', 'imagesReportUrl', 'beforeReportUrl'];

      for (const item of this.filteredProducts) {
        for (const [configId, metric] of Object.entries(item.metrics || {})) {
          const field = fields.find(f => metric?.[f] === reportUrl);

          if (field) {
            return { configId, field };
          }
        }
      }

      return null;
    },

    // Dashboard order and the retailer/brand/category filters exist only client-side, so the server cannot rebuild this.
    writePdpNavPayload(reportUrl) {
      // PdpReport rows open /optix?product= in PDPAnalysis.vue, which cannot render this nav.
      if (reportUrl.includes('/optix')) {
        this.clearPdpNavPayload();

        return;
      }

      const clicked = this.findClickedMetric(reportUrl);

      if (!clicked) {
        this.clearPdpNavPayload();

        return;
      }

      const listNames = new Map((window.optixProductLists || []).map(list => [Number(list.id), list.name]));
      const clickedPath = removeUrlOriginLocation(reportUrl);
      const items = [];

      this.filteredProducts.forEach((item) => {
        const url = item.metrics?.[clicked.configId]?.[clicked.field];

        if (!url || url.includes('/optix')) {
          return;
        }

        const listId = Number(item.product_list_id);

        items.push({
          url: removeUrlOriginLocation(url),
          label: item.url || item.product_name || '',
          reportName: item.product_title || item.product_name || '',
          productListId: listId,
          productListName: listNames.get(listId) || '',
        });
      });

      // The report matches itself by pathname, so a stale payload is ignored rather than misapplied.
      if (items.length < 2 || !items.some(item => item.url === clickedPath)) {
        this.clearPdpNavPayload();

        return;
      }

      try {
        sessionStorage.setItem(this.pdpNavStorageKey(), JSON.stringify({ scope: 'dashboard', items }));
      } catch (e) {
        this.clearPdpNavPayload();
      }
    },

    clearPdpNavPayload() {
      try {
        sessionStorage.removeItem(this.pdpNavStorageKey());
      } catch (e) {
        // do nothing
      }
    },

    openReportInFrame(reportUrl, reportName = null, reportId = null, reportUuid = null, isImageTab = false) {
      if (!reportUrl) {
        return;
      }

      this._didPushReportHistory = false;
      this.writePdpNavPayload(reportUrl);

      let isPdp = false;
      if (reportUrl.includes('/optix')) {
        reportUrl += '&embedded_dashboard=1';

        if (isImageTab) {
          reportUrl += '&image_tab=1'
        }
        isPdp = true;
      } else {
        reportUrl += '?embedded_dashboard=1';
      }

      const reportsTabPane = document.querySelector('#reports');
      if (!reportsTabPane) {
        console.error('Reports tab pane not found');
        return;
      }

      const livewireComponent = reportsTabPane.querySelector('[wire\\:id]');
      const existingGrid = document.getElementById('analyticsGridContainer');
      const table = document.querySelector('table.table:not(.product-table):not(.dashboard-table)');
      // const tableWrapper = document.querySelector('.table-responsive');
      const productContainer = document.querySelector('.product-card-container');

      if (livewireComponent) livewireComponent.style.display = 'none';
      if (existingGrid) existingGrid.style.display = 'none';
      if (table) table.style.display = 'none';
      // if (tableWrapper) tableWrapper.style.display = 'none';
      if (productContainer) productContainer.style.display = 'none';

      const headerControls = reportsTabPane.querySelector('.d-flex.justify-content-end.align-items-center.mb-3');
      if (headerControls) {
        headerControls.style.setProperty('display', 'none', 'important');
      }

      const mainTabs = document.getElementById('optixMainTabs');
      if (mainTabs) {
        mainTabs.style.setProperty('display', 'none', 'important');
      }

      const cardBody = document.querySelector('.card-body');
      if (cardBody) {
        cardBody.style.setProperty('padding', '0', 'important');
      }

      const reportsTitleDiv = reportsTabPane.querySelector('div > h5.font-weight-bold.mb-1')?.parentElement;
      if (reportsTitleDiv) {
        reportsTitleDiv.style.setProperty('display', 'none', 'important');
      }

      const reportIdentifier = reportUuid || reportId;
      if (reportIdentifier && !isPdp) {
        const url = new URL(window.location.href);
        url.searchParams.set('report', reportIdentifier);
        history.pushState(
          { reportUuid, reportId },
          '',
          url.toString()
        );
        this._didPushReportHistory = true;
      }

      this.activeReportName = reportName;
      this.activeReportId = reportId;
      this.activeReportUuid = reportUuid;
      this.isReportLoading = true;

      if (this.activeReportUrl === reportUrl) {
        this.activeReportUrl = 'about:blank';

        this.$nextTick(() => {
          setTimeout(() => {
            this.activeReportUrl = reportUrl;
          }, 10);
        });

        return;
      }

      this.activeReportUrl = reportUrl;
    },

    handleIframeLoad() {
      this.isReportLoading = false;

      this.$nextTick(() => {
        const iframe = document.getElementById('reportIframe');
        if (!iframe) {
          return;
        }

        try {
          const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!iframeDoc) {
            return;
          }

          const body = iframeDoc.body;
          const html = iframeDoc.documentElement;

          if (body && html) {
            const height = Math.max(
              body.scrollHeight,
              body.offsetHeight,
              html.clientHeight,
              html.scrollHeight,
              html.offsetHeight
            );

            iframe.style.height = `${Math.max(height, window.innerHeight * 0.8)}px`;
          }
        } catch (e) {
          iframe.style.height = '80vh';
        }
      });
    },

    closeReportFrame(skipHistoryUpdate = false) {
      this.clearPdpNavPayload();
      this.activeReportUrl = null;
      this.activeReportName = null;
      this.activeReportId = null;
      this.activeReportUuid = null;
      this.isReportLoading = false;
      this._didPushReportHistory = false;

      const reportsTabPane = document.querySelector('#reports');
      const livewireComponent = reportsTabPane?.querySelector('[wire\\:id]');
      const existingGrid = document.getElementById('analyticsGridContainer');
      const table = document.querySelector('table.table:not(.product-table):not(.dashboard-table)');
      const tableWrapper = document.querySelector('.table-responsive');
      const productContainer = document.querySelector('.product-card-container');
      const headerControls = reportsTabPane?.querySelector('.d-flex.justify-content-end.align-items-center.mb-3');
      const mainTabs = document.getElementById('optixMainTabs');
      const cardBody = document.querySelector('.card-body');
      const reportsTitleDiv = reportsTabPane?.querySelector('div > h5.font-weight-bold.mb-1')?.parentElement;

      if (livewireComponent) livewireComponent.style.display = '';
      if (existingGrid) existingGrid.style.display = '';
      const gridView = window.analyticsGridView;
      const isGridOrPanel = gridView && (gridView.currentView === 'grid' || gridView.currentView === 'panel');
      if (table && !isGridOrPanel) table.style.display = '';
      // if (tableWrapper) tableWrapper.style.display = '';
      if (productContainer) productContainer.style.display = '';
      if (headerControls) headerControls.style.removeProperty('display');
      if (mainTabs) mainTabs.style.removeProperty('display');
      if (cardBody) cardBody.style.removeProperty('padding');
      if (reportsTitleDiv) reportsTitleDiv.style.removeProperty('display');

      if (!skipHistoryUpdate) {
        const url = new URL(window.location.href);
        url.searchParams.delete('report');
        history.pushState({}, '', url.toString());
      }
    },

    updateScores() {
      axios.get('/analytics-history/update-scores/');
      toastr.success('Updating Scores...');
    },

    refreshReports() {
      this.showRefreshAllConfirm = true;
    },

    confirmRefreshAllReports() {
      this.showRefreshAllConfirm = false;
      this.runRefreshReports();
    },

    cancelRefreshAllReports() {
      this.showRefreshAllConfirm = false;
    },

    runRefreshReports() {
      const params = new URLSearchParams();
      params.set('include_individual_items', this.showIndividualItems ? '1' : '0');
      if (this.selectedDashboardConfig) {
        params.set('dashboard_config_id', this.selectedDashboardConfig);
      }

      if (
        this.allProductLists.length > 0
        && Array.isArray(this.selectedProductList)
        && this.selectedProductList.length > 0
      ) {
        this.selectedProductList.forEach((id) => {
          params.append('product_list_ids[]', id);
        });
      }

      const query = params.toString();

      axios.get(`/analytics-history/refresh-all-reports/${query ? `?${query}` : ''}`);
      toastr.success('Updating reports...');
    },

    escapeTooltipHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    kpiCardTooltip(card) {
      const name = this.escapeTooltipHtml(card.name);
      const selectedText = this.checkedProductIds.length
        ? 'Selected products'
        : 'All visible products';

      if (this.kpiShowCount) {
        return `
          <div class="kpi-tooltip-content">
            <div class="kpi-tooltip-title">${name}</div>
            <div class="kpi-tooltip-row">
              <span>Below threshold</span>
              <strong>${card.needCount} of ${card.total}</strong>
            </div>
            <div class="kpi-tooltip-row">
              <span>Threshold</span>
              <strong>${card.threshold}%</strong>
            </div>
            <div class="kpi-tooltip-context">${selectedText}</div>
          </div>
        `;
      }

      return `
        <div class="kpi-tooltip-content">
          <div class="kpi-tooltip-title">${name}</div>
          <div class="kpi-tooltip-row">
            <span>Average score</span>
            <strong>${card.percent}%</strong>
          </div>
          <div class="kpi-tooltip-row">
            <span>Reports included</span>
            <strong>${card.total}</strong>
          </div>
          <div class="kpi-tooltip-context">${selectedText}</div>
        </div>
      `;
    },

    initKpiTooltips() {
      if (
        typeof window.$ === 'undefined' ||
        typeof window.$.fn === 'undefined' ||
        typeof window.$.fn.tooltip !== 'function'
      ) {
        return;
      }

      const $tooltips = window.$(this.$el).find(
        '.kpi-card[data-toggle="tooltip"]'
      );

      $tooltips.tooltip('dispose');
      $tooltips.tooltip({
        container: 'body',
        trigger: 'hover focus',
        delay: {
          show: 120,
          hide: 50,
        },
      });
    },

    destroyKpiTooltips() {
      if (
        typeof window.$ === 'undefined' ||
        typeof window.$.fn === 'undefined' ||
        typeof window.$.fn.tooltip !== 'function'
      ) {
        return;
      }

      window.$(this.$el)
        .find('.kpi-card[data-toggle="tooltip"]')
        .tooltip('dispose');
    },

    toggleKpiDisplay() {
      this.kpiShowCount = !this.kpiShowCount;

      this.$nextTick(() => {
        this.initKpiTooltips();
      });
    },

    kpiCardLabel(name) {
      return String(name).trim().replace(/\s*\/\s*/g, '/').split(/\s+/)[0];
    },

    clearCheckedProducts() {
      this.checkedProductIds = [];
      if (this.optimizeCatalogProductFilter === 'selected') {
        this.optimizeCatalogProductFilter = 'all';
      }
    },

    toggleRowCheck(rowKey) {
      const idx = this.checkedProductIds.indexOf(rowKey);
      if (idx === -1) {
        this.checkedProductIds.push(rowKey);
      } else {
        this.checkedProductIds.splice(idx, 1);
        if (this.checkedProductIds.length === 0 && this.optimizeCatalogProductFilter === 'selected') {
          this.optimizeCatalogProductFilter = 'all';
        }
      }
    },

    toggleAllChecked() {
      if (this.allChecked) {
        this.checkedProductIds = [];
        if (this.optimizeCatalogProductFilter === 'selected') {
          this.optimizeCatalogProductFilter = 'all';
        }
      } else {
        this.checkedProductIds = this.filteredProducts.map(p => p.rowKey);
      }
    },

    openOptimizeCatalogModal(fromSelection = false) {
      if (fromSelection) {
        this.optimizeCatalogProductFilter = 'selected';
      } else if (this.optimizeCatalogProductFilter === 'selected') {
        this.optimizeCatalogProductFilter = 'all';
      }
      this.optimizeCatalogView = 'per_sku';
      this.optimizeCatalogGroupName = '';
      if (!this.optimizeCatalogDueDate) {
        this.setOptimizeCatalogDatePreset('This Friday');
      }
      this.initOptimizeCatalogSkuSelections();
      this.showOptimizeCatalogModal = true;
    },

    closeOptimizeCatalogModal() {
      this.showOptimizeCatalogModal = false;
      this.optimizeIssueOwnerDropdownId = null;
      this.optimizeDefaultOwnerDropdownOpen = false;
      this.optimizeProductDropdownOpen = false;
    },
    buildBrief()
    {
      this.showContentBriefModal = true;
    },
    closeContentBriefModal() {
      this.showContentBriefModal = false;
    },
    async createBriefFromModal({ briefPayload }) {
      try {
        const { data } = await axios.post('/briefs/api/briefs', briefPayload);
        const briefTitle = data?.brief?.title || briefPayload?.title || 'Brief';

        toastr.success(`Created brief: ${briefTitle}`);
        window.location.href = `/briefs`;
      } catch (error) {
        const message = error?.response?.data?.message || 'Failed to create brief. Please try again.';
        toastr.error(message);
      }
    },
    toggleOptimizeIssue(issue) {
      issue.selected = !issue.selected;
    },

    toggleOptimizeIssueOwnerDropdown(issueId) {
      this.optimizeDefaultOwnerDropdownOpen = false;
      this.optimizeProductDropdownOpen = false;
      this.optimizeIssueOwnerDropdownId = this.optimizeIssueOwnerDropdownId === issueId ? null : issueId;

      if (this.optimizeIssueOwnerDropdownId) {
        this.$nextTick(() => {
          this.positionOptimizeDropdown(event.currentTarget);
        });
      }
    },

    positionOptimizeDropdown(triggerEl) {
      const rect = triggerEl.getBoundingClientRect();
      this.optimizeDropdownStyle = {
        position: 'fixed',
        top: `${rect.bottom + 2}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: 9999,
      };
    },

    closeOptimizeIssueOwnerDropdown() {
      this.optimizeIssueOwnerDropdownId = null;
    },

    selectOptimizeIssueOwner(issue, ownerId) {
      this.setOptimizeIssueOwner(issue, ownerId);
      this.optimizeIssueOwnerDropdownId = null;
    },

    toggleOptimizeDefaultOwnerDropdown() {
      this.optimizeProductDropdownOpen = false;
      this.optimizeIssueOwnerDropdownId = null;
      this.optimizeDefaultOwnerDropdownOpen = !this.optimizeDefaultOwnerDropdownOpen;
    },

    closeOptimizeDefaultOwnerDropdown() {
      this.optimizeDefaultOwnerDropdownOpen = false;
    },

    selectOptimizeDefaultOwner(ownerId) {
      this.optimizeCatalogDefaultOwnerId = Number(ownerId);

      this.optimizeCatalogIssues.forEach((issue) => {
        if (!issue.ownerTouched) {
          issue.authorId = this.optimizeCatalogDefaultOwnerId;
        }
      });
      Object.keys(this.optimizeCatalogSkuOwners).forEach(key => {
        this.optimizeCatalogSkuOwners[key] = this.optimizeCatalogDefaultOwnerId;
      });
      
      this.optimizeDefaultOwnerDropdownOpen = false;
    },

    setOptimizeIssueOwner(issue, selectedOwnerId) {
      const parsedId = Number(selectedOwnerId);

      issue.ownerTouched = true;
      issue.authorId = Number.isNaN(parsedId) ? null : parsedId;
    },

    getOptimizeAuthorName(authorId) {
      const owner = this.optimizeCatalogAuthors.find((author) => author.id === Number(authorId));
      return owner ? owner.fullName : 'Unassigned';
    },

    optimizeWithAiPerSku() {
      toastr.info('Coming soon');
    },

    optimizeWithAiGroup() {
      toastr.info('Coming soon');
    },

    computePresetDate(label) {
      const today = new Date();
      const day = today.getDay();
      let target = new Date(today);
      if (label === 'This Friday') {
        const diff = (5 - day + 7) % 7 || 7;
        target.setDate(today.getDate() + diff);
      } else if (label === 'Next Monday') {
        const diff = (1 - day + 7) % 7 || 7;
        target.setDate(today.getDate() + diff);
      } else if (label === 'End of month') {
        target = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      } else if (label === 'End of quarter') {
        const quarter = Math.floor(today.getMonth() / 3);
        target = new Date(today.getFullYear(), (quarter + 1) * 3, 0);
      }
      const y = target.getFullYear();
      const m = String(target.getMonth() + 1).padStart(2, '0');
      const d = String(target.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },

    setOptimizeCatalogDatePreset(label) {
      this.optimizeCatalogDueDate = this.computePresetDate(label);
      this.optimizeCatalogPeriod = label;
      this.showOptimizeCatalogDatePicker = false;
    },

    optimizeCatalogDatePickerPrev() {
      const d = new Date(this.optimizeCatalogDatePickerView);
      d.setDate(1);
      d.setMonth(d.getMonth() - 1);
      this.optimizeCatalogDatePickerView = d;
    },

    optimizeCatalogDatePickerNext() {
      const d = new Date(this.optimizeCatalogDatePickerView);
      d.setDate(1);
      d.setMonth(d.getMonth() + 1);
      this.optimizeCatalogDatePickerView = d;
    },

    pickOptimizeCatalogDate(day) {
      const d = day.date;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      this.optimizeCatalogDueDate = `${yyyy}-${mm}-${dd}`;
      this.optimizeCatalogPeriod = this.optimizeCatalogDueDateLabel;
      this.showOptimizeCatalogDatePicker = false;
    },

    isProductFailing(product) {
      return this.kpiCards.some(card => {
        const score = this.getProductKpiScore(product, card);
        return score !== null && score < 50;
      });
    },

    isProductWarningOrFailing(product) {
      return this.kpiCards.some(card => {
        const score = this.getProductKpiScore(product, card);
        return score !== null && score < SCORE_PASS_THRESHOLD;
      });
    },

    getProductKpiScore(product, kpiCard) {
      const cardId = String(kpiCard.id);
      if (cardId.includes(':')) {
        const [configId, fieldKey] = cardId.split(':');
        const metric = product.metrics[configId];
        if (!metric || !Array.isArray(metric.customFields)) return null;
        const cf = metric.customFields.find(item => item.key === fieldKey);
        if (!cf) return null;
        return this.normalizeKpiMetricValue(cf.rawValue ?? cf.value ?? cf.numericValue);
      }
      const config = this.orderedConfigs.find(c => String(c.id) === cardId);
      const metric = product.metrics[kpiCard.id];
      if (!metric) return null;
      if (config && Number(config.is_pdp)) {
        const vals = [
          this.normalizeKpiMetricValue(metric.textNumeric),
          this.normalizeKpiMetricValue(metric.imagesNumeric),
        ].filter(v => v !== null);
        return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
      }
      return this.normalizeKpiMetricValue(metric.afterNumeric);
    },

    getProductKpiActive(product, kpiCard) {
      const score = this.getProductKpiScore(product, kpiCard);
      return score !== null && score < SCORE_PASS_THRESHOLD;
    },

    getProductOverallScore(product) {
      const scores = this.kpiCards
        .map(card => this.getProductKpiScore(product, card))
        .filter(s => s !== null);
      if (!scores.length) return null;
      return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
    },

    initOptimizeCatalogSkuSelections() {
      this.optimizeCatalogSkuSelections = {};
      this.optimizeCatalogSkuOwners = {};
      for (const product of this.filteredProducts) {
        const selections = {};
        for (const card of this.kpiCards) {
          selections[card.id] = this.getProductKpiActive(product, card);
        }
        this.$set(this.optimizeCatalogSkuSelections, product.rowKey, selections);
        this.$set(this.optimizeCatalogSkuOwners, product.rowKey, this.optimizeCatalogDefaultOwnerId);
      }
    },

    toggleSkuIssue(rowKey, kpiCardId) {
      if (!this.optimizeCatalogSkuSelections[rowKey]) {
        this.$set(this.optimizeCatalogSkuSelections, rowKey, {});
      }
      const current = this.optimizeCatalogSkuSelections[rowKey][kpiCardId] ?? false;
      this.$set(this.optimizeCatalogSkuSelections[rowKey], kpiCardId, !current);
    },

    toggleAllForIssue(kpiCardId) {
      const counts = this.optimizeCatalogToggleAllCounts[kpiCardId];
      const newVal = !(counts && counts.active === counts.total && counts.total > 0);
      for (const product of this.optimizeCatalogModalProducts) {
        if (!this.optimizeCatalogSkuSelections[product.rowKey]) {
          this.$set(this.optimizeCatalogSkuSelections, product.rowKey, {});
        }
        this.$set(this.optimizeCatalogSkuSelections[product.rowKey], kpiCardId, newVal);
      }
    },

    openSkuBriefPreviewModal() {
      this.optimizeCatalogSkuBriefs = this.buildSkuBriefPreviews();
      this.skuBriefResults = []; // reset per-open so retry state is scoped to one modal session
      this.showOptimizeCatalogDatePicker = false;
      this.showOptimizeCatalogToggleAll = false;
      this.showSkuBriefPreviewModal = true;
    },

    closeSkuBriefPreviewModal() {
      this.showSkuBriefPreviewModal = false;
    },

    buildSkuBriefPreviews() {
      return this.optimizeCatalogModalProducts
        .filter(product => {
          const sel = this.optimizeCatalogSkuSelections[product.rowKey];
          return sel && Object.values(sel).some(Boolean);
        })
        .map(product => {
          const activeCards = this.kpiCards.filter(
            card => this.optimizeCatalogSkuSelections[product.rowKey]?.[card.id]
          );
          const ownerId = this.optimizeCatalogSkuOwners[product.rowKey] ?? this.optimizeCatalogDefaultOwnerId;
          const reportUrl = (() => {
            const candidates = [
              ...activeCards,
              ...this.kpiCards.filter(c => !activeCards.includes(c)),
            ];
            for (const card of candidates) {
              const cardId = String(card.id);
              const configId = cardId.includes(':') ? cardId.split(':')[0] : cardId;
              const m = product.metrics?.[configId];
              if (!m) continue;
              const url = m.reportUrl || m.textReportUrl || m.imagesReportUrl;
              if (url) return url;
            }
            return null;
          })();
          const deliverablesLines = activeCards.map((card, i) => `${i + 1}. ${card.name}`);
          if (reportUrl) {
            deliverablesLines.push('');
            deliverablesLines.push(`Reference: PDP report — ${reportUrl}`);
          }
          return {
            rowKey: product.rowKey,
            title: product.product_name,
            productId: product.product_id,
            isAsin: product.is_asin,
            delivery_date: this.optimizeCatalogDueDate,
            ownerId,
            assigneeType: 'specific_person',
            assigneeDefaultName: this.getOptimizeAuthorName(ownerId),
            assigneeName: this.getOptimizeAuthorName(ownerId),
            overviewText: this.buildSkuOverviewText(product.product_name, activeCards),
            deliverablesText: deliverablesLines.join('\n'),
          };
        });
    },

    async createAllSkuBriefs({ drafts }) {
      const targets = this.skuBriefResults.some(r => !r.success)
        ? drafts.filter(d => {
          const prev = this.skuBriefResults.find(r => r.rowKey === d.rowKey);
          return !prev || !prev.success;
        })
        : drafts;

      targets.forEach(d => this.upsertSkuBriefResult({ rowKey: d.rowKey, success: null, error: null }));

      this.isCreatingBriefs = true;
      toastr.info(`Creating ${targets.length} ${targets.length === 1 ? 'brief' : 'briefs'}...`);

      for (const draft of targets) {
        const assignee = draft.assigneeName?.trim() || null;
        const payload = {
          title: draft.title,
          summary: draft.overviewText,
          delivery_date: draft.delivery_date,
          assignee,
          status: assignee ? 'assigned' : 'draft',
          visibility: 'team',
          created_via: 'ui',
          payload: {
            source: 'optimize_catalog_per_sku',
            group_name: this.optimizeCatalogGroupName || null,
            section_labels: {
              custom_optix_overview: 'Overview',
              deliverables: 'Deliverables',
            },
          },
          sections: [
            { type_id: 'custom_optix_overview', content: draft.overviewText, visible: true, sort_order: 0 },
            { type_id: 'deliverables', content: draft.deliverablesText, visible: true, sort_order: 1 },
            { type_id: 'schedule', content: '(Suggested) Kickoff this week · creative review in 2 weeks · final assets 4 weeks out, ahead of the seasonal flight.', visible: true, sort_order: 2 },
          ],
        };

        try {
          await axios.post('/briefs/api/briefs', payload);
          this.upsertSkuBriefResult({ rowKey: draft.rowKey, success: true, error: null });
        } catch (error) {
          const msg = error?.response?.data?.message || 'Failed to create brief.';
          this.upsertSkuBriefResult({ rowKey: draft.rowKey, success: false, error: msg });
        }
      }

      this.isCreatingBriefs = false;

      const targetKeys = new Set(targets.map(t => t.rowKey));
      const targetResults = this.skuBriefResults.filter(r => targetKeys.has(r.rowKey));
      const failed = targetResults.filter(r => !r.success).length;
      const succeeded = targetResults.filter(r => r.success).length;

      if (failed === 0) {
        toastr.success(`${succeeded} ${succeeded === 1 ? 'brief' : 'briefs'} created`);
        this.closeSkuBriefPreviewModal();
        this.closeOptimizeCatalogModal();
      } else {
        toastr.error(`${succeeded} created, ${failed} failed`);
      }

      window.location.href = `/briefs`;
    },

    buildSkuOverviewText(productName, activeCards) {
      const count = activeCards.length;
      const focusAreas = activeCards.map(c => c.name).join(', ');
      return `Scope: ${productName}.\n${count} ${count === 1 ? 'workstream' : 'workstreams'} across 1 product touchpoint.\nFocus areas: ${focusAreas}.`;
    },

    upsertSkuBriefResult(result) {
      const idx = this.skuBriefResults.findIndex(r => r.rowKey === result.rowKey);
      if (idx === -1) {
        this.skuBriefResults.push(result);
      } else {
        this.$set(this.skuBriefResults, idx, result);
      }
    },

    download() {
      window.downloadProductsData();
    },
    openSendToEmailModal() {
      this.sendEmailForm = {
        allConfigs: true,
        selectedConfigs: [...this.sendEmailConfigOptions],
        onlyIssueProducts: false,
        threshold: 70,
        summaryTemplate: '{{notOptimizedProducts}} of your {{totalProducts}} products are not mobile optimized',
        recipients: '',
        fields: ''
      };

      $('#sendToEmailModal').modal('show');
    },

    closeSendToEmailModal() {
      $('#sendToEmailModal').modal('hide');
    },

    parseTextareaList(value) {
      if (!value) {
        return [];
      }

      return String(value)
        .split(/[\n,]+/)
        .map(item => item.trim())
        .filter(Boolean);
    },

    toggleAllConfigs() {
      if (this.sendEmailForm.allConfigs) {
        this.sendEmailForm.selectedConfigs = [...this.sendEmailConfigOptions];
        return;
      }

      this.sendEmailForm.selectedConfigs = [];
    },

    async sendProductsReportEmail() {
      const recipients = this.parseTextareaList(this.sendEmailForm.recipients);
      const configIds = this.getSelectedConfigIds();

      if (!recipients.length) {
        toastr.error('Please enter at least one email recipient.');
        return;
      }

      if (!configIds.length) {
        toastr.error('Please select at least one config.');
        return;
      }

      const threshold = Number(this.sendEmailForm.threshold);

      if (this.sendEmailForm.onlyIssueProducts) {
        if (Number.isNaN(threshold) || threshold < 0 || threshold > 100) {
          toastr.error('Threshold must be between 0 and 100.');
          return;
        }
      }

      this.isSendingEmail = true;

      try {
        await axios.post('/analytics-history/send-products-report-email', {
          config_ids: configIds,
          only_issue_products: this.sendEmailForm.onlyIssueProducts ? 1 : 0,
          threshold: this.sendEmailForm.onlyIssueProducts ? threshold : null,
          summary_template: this.sendEmailForm.summaryTemplate,
          recipients,
          before_range: this.beforeRange,
          before_date: this.beforeCustomDate,
          after_type: this.afterType,
          after_date: this.afterCustomDate,
          product_list_ids: this.selectedProductList || [],
          include_individual_items: this.showIndividualItems,
        });

        toastr.success('Report email has been queued.');
        this.closeSendToEmailModal();
      } catch (error) {
        console.error('Failed to send report email:', error);
        toastr.error(error?.response?.data?.message || 'Failed to send report email.');
      } finally {
        this.isSendingEmail = false;
      }
    },
    getSelectedConfigIds() {
      if (this.sendEmailForm.allConfigs) {
        return this.orderedConfigs.map(config => config.id);
      }

      return (this.sendEmailForm.selectedConfigs || []).map(item => item.value);
    },
    handleSelectedConfigsChange(selectedConfigs) {
      const selectedCount = Array.isArray(selectedConfigs) ? selectedConfigs.length : 0;
      const totalCount = this.sendEmailConfigOptions.length;

      this.sendEmailForm.allConfigs = totalCount > 0 && selectedCount === totalCount;
    },
    toggleSort(key) {
      if (this.sortKey !== key) {
        this.sortKey = key;
        this.sortDir = 'desc';
        return;
      }

      if (this.sortDir === 'desc') {
        this.sortDir = 'asc';
        return;
      }

      this.sortKey = null;
      this.sortDir = 'desc';
    },

    getSortValue(item, key) {
      if (key === 'product') {
        return String(item.product_name || '').toLowerCase();
      }

      if (key === 'optix') {
        return this.normalizePercent(item.optix_score);
      }

      const [type, configId, fieldKey] = key.split(':');
      const metric = item.metrics && item.metrics[configId];

      if (!metric) {
        return 0;
      }

      if (type === 'custom') {
        const field = (metric.customFields || []).find(f => f.key === fieldKey);
        return field ? (field.numericValue ?? 0) : 0;
      }

      return Number(metric[NUMERIC_FIELD_BY_SORT_TYPE[type]]) || 0;
    },
  }
};
</script>

<style scoped>
.optix-dashboard {
  --muted: 210 40% 96.1%;
  background: #fff;
}

.dashboard-card {
  background: #ffffff;
  border: 1px solid #e9edf5;
  border-radius: 18px;
  padding: 16px;
}

.gap-12 {
  gap: 12px;
}

.dashboard-controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: -4px;
}


.filters-popover-wrap {
  position: relative;
  z-index: 30;
}

.btn-filters,
.btn-compare {
  height: 36px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #111827;
  font-weight: 700;
  font-size: 12px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);

  svg {
    margin-right: 3px;
  }
}

.btn-filters:hover,
.btn-filters.active,
.btn-compare:hover {
  background: #f8fafc;
  color: #111827;
  border-color: #d5dde8;
}

.btn-compare.active {
  background-color: hsl(168, 76%, 42%);
  color: #fff;
  border-color: hsl(168, 76%, 42%);
}

.filter-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  min-width: 16px;
  padding: 0 4px;
  margin-left: 2px;
  border-radius: 9999px;
  background: #e2e8f0;
  color: #334155;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
}

.compare-controls {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.compare-filter-inline {
  gap: 8px;
}

.compare-vs-label {
  color: #94a3b8;
  font-size: 14px;
  font-weight: 700;
}

.compare-date-picker {
  position: relative;
}

.compare-date-picker .date-picker-popover {
  right: 0;
  left: auto;
}


.view-mode-toggle {
  display: inline-flex;
  align-items: center;
  height: 36px;
  padding: 2px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: rgba(241, 245, 249, 0.4);
}

.view-mode-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 8px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s, background 0.15s;

  &:hover {
    color: #111827;
  }

  &.active {
    background: #fff;
    color: #111827;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }
}

.compact-head-row th {
  font-size: 12px!important;
  padding: 13px 12px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: #fafbfd!important;
}


.search-outer {
  position: relative;
}

.search-error-msg {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 20;
  background: #fff;
  border-radius: 3px;
  padding: 2px 6px;
  font-size: 11px;
  color: #dc3545;
  white-space: nowrap;
  margin-top: 2px;
  pointer-events: none;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}

.search-clear-btn {
  position: absolute;
  top: 12px;
  right: 16px;
  background: none;
  border: none;
  padding: 0;
  line-height: 1;
  font-size: 10px;
  color: #94a3b8;
  cursor: pointer;
}

.search-clear-btn:hover {
  color: #64748b;
}

.search-wrap {
  position: relative;
}

.search-wrap > i {
  position: absolute;
  top: 6px;
  left: 12px;
  color: #94a3b8;
}

.search-input {
  padding-left: 32px;
  padding-right: 16px;
  min-height: 36px;
  width: 180px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  font-size: 14px;
  resize: none;
  overflow: hidden;
  overflow-y: auto;
  max-height: 120px;
  line-height: 1.5;
}


.btn-icon {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #334155;

  &:hover {
    background-color: hsl(210 40% 96.1%);
    color: hsl(222.2 47.4% 11.2%);
  }
}

.filter-inline {
  display: flex;
  align-items: center;
  gap: 10px;
}

.filter-label,
.vs-label {
  color: #677489;
  font-weight: 600;
  font-size: 13px;
}

.filter-select {
  min-width: 160px;
  height: 40px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: none;
  font-size: 14px;
}



.dashboard-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  background: #fff;
  padding: 48px 16px 56px;
}

.empty-state-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 9999px;
  background: #f1f5f9;
  color: #64748b;
}

.empty-state-title {
  margin: 16px 0 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  text-align: center;
}

.empty-state-picker {
  position: relative;
  z-index: 20;
  margin-top: 16px;
}

.empty-state-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 16px;
  border-radius: 10px;
  background: #209c86;
  color: #fff;
  font-size: 14px;
  font-weight: 600;

  &:hover,
  &:focus {
    background: #1b8473;
    color: #fff;
  }
}

.empty-state-popover {
  left: 50%;
  transform: translateX(-50%);
}

.dashboard-table-wrap {
  border: 1px solid #eef2f7;
  border-radius: 16px;
  overflow: auto;
}

.dashboard-table {
  margin: 0;
  background: #fff;
  min-width: 900px;
}

.dashboard-table thead th {
  background: #fff;
  border-top: 0;
  border-bottom: 1px solid #edf2f7;
  color: #677489;
  font-weight: 700;
  vertical-align: middle;
  /* Sticky column headers; per-row `top` offsets set by the sticky-table-scroll util. */
  position: sticky;
  top: 0;
  z-index: 4;
  box-shadow: inset 0 -1px 0 #edf2f7;
}

.top-head-row th {
  font-size: 14px;
  padding: 18px 12px;
}

.sub-head-row th {
  font-size: 14px;
  padding: 14px 20px;
  color: #94a3b8;
  font-weight: 600;
}

.group-head {
  text-align: center;
}

.config-head-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}

.product-col {
  width: 360px;
  text-align: left;
}

.metric-col {
  width: 110px;
  text-align: center;
}

.delta-col {
  width: 80px;
  text-align: center;
}

.group-divider-left {
  border-left: 1px solid #edf2f7 !important;
}

.dashboard-table tbody td {
  padding: 10px;
  border-top: 1px solid #f1f5f9;
  vertical-align: middle;
  background: #fff;
  transition: background-color 150ms ease;
}

.dashboard-table tbody tr:hover td {
  background-color: hsl(var(--muted) / 0.5);
}

.dashboard-table tbody tr[data-selected="true"] td {
  background-color: #ecfdf599;
}

.product-cell {
  min-width: 275px;
}

.product-thumb {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e2e8f0;
  margin-right: 12px;
  flex-shrink: 0;
}

.product-meta {
  min-width: 0;
  flex-direction: column;
  align-items: flex-start!important;
  gap: 0!important;
}

.product-name {
  color: #111827;
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
  max-width: 380px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-brand {
  color: #677489;
  font-size: 13px;
  margin-top: 3px;
}

.metric-cell,
.delta-cell {
  text-align: center;
  white-space: nowrap;
}

.score-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 45px;
  height: 25px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 700;
}

.score-pill:hover {
  text-decoration: none;
}

.score-pill-light {
  background: #ffffff;
  color: #111827;
  border: 1px solid #e5e7eb;
}

.score-pill-green {
  background: #58c06c;
  color: #ffffff;
}

.score-pill-yellow {
  background: #d9ab2f;
  color: #ffffff;
}

.score-pill-gray {
  background: #e3e3e3;
  color: #ffffff;
}

.score-pill-red {
  background: #e45858;
  color: #ffffff;
}

.delta-up,
.delta-down,
.delta-neutral {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  font-weight: 700;
}

.delta-up {
  color: #16a34a;
}

.delta-down {
  color: #dc2626;
}

.delta-neutral {
  color: #677489;
}

.chart-placeholder {
  border: 1px dashed #cbd5e1;
  border-radius: 16px;
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

.chart-placeholder-inner {
  text-align: center;
  color: #677489;
  font-weight: 600;
}

.chart-placeholder-inner i {
  font-size: 42px;
}

@media (max-width: 1199.98px) {
  .search-wrap {
    min-width: 220px;
  }
}

@media (max-width: 991.98px) {
  .dashboard-card {
    padding: 16px;
  }

  .product-name {
    font-size: 15px;
  }
}

.dashboard-actions-dropdown .dropdown-menu {
  min-width: 220px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  padding: 6px;
}

.dashboard-actions-dropdown .dropdown-item {
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  padding: 10px 12px;
}

.dashboard-actions-dropdown .dropdown-item:hover,
.dashboard-actions-dropdown .dropdown-item:focus {
  background: #f8fafc;
  color: #111827;
}

.no-caret::after {
  display: none !important;
}

.config-custom-icon,
.config-lucide-icon {
  width: 20px;
  height: 20px;
  opacity: 0.6;
  flex-shrink: 0;
}

.report-details-container {
  width: 100%;
  background: #fff;
}

.report-iframe-container {
  position: relative;
  width: 100%;
}

.report-loading-overlay {
  position: absolute;
  inset: 0;
  background: #f8f9fa;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.report-loading-content {
  text-align: center;
  color: #6c757d;
}

.report-iframe {
  width: 100%;
  height: 80vh;
  border: none;
  border-radius: 0;
  display: block;
}

.optix-email-modal .modal-dialog {
  max-width: 760px;
}

.optix-email-modal .modal-content {
  border: 0;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.18);
}

.optix-email-modal .modal-header {
  padding: 22px 28px;
  border-bottom: 1px solid #e5e7eb;
}

.optix-email-modal .modal-title {
  font-size: 24px;
  font-weight: 700;
  color: #3b4963;
  display: flex;
  align-items: center;
}

.optix-email-modal .close {
  font-size: 34px;
  line-height: 1;
  color: #6b7280;
  opacity: 1;
  text-shadow: none;
  outline: none;
  box-shadow: none;
}

.optix-email-modal .modal-body {
  padding: 28px;
}

.optix-email-modal .modal-footer {
  padding: 20px 28px 28px;
  border-top: 1px solid #e5e7eb;
}

.send-email-select,
.send-email-textarea,
.optix-email-modal .form-control {
  border-radius: 12px;
  border: 1px solid #d7dee8;
  box-shadow: none;
  font-size: 15px;
}

.send-email-select,
.optix-email-modal input.form-control {
  height: 44px;
}

.send-email-textarea {
  min-height: 120px;
  resize: vertical;
  padding: 14px 16px;
}

.optix-email-modal label {
  font-size: 16px;
  color: #3b4963;
  margin-bottom: 10px;
}

.btn-modal-cancel,
.btn-modal-send {
  height: 46px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 15px;
  padding: 0 20px;
  border: 0;
}

.btn-modal-cancel {
  background: #6b7280;
  color: #fff;
}

.btn-modal-cancel:hover {
  background: #5b6270;
  color: #fff;
}

.btn-modal-send {
  background: #4da3bb;
  color: #fff;
}

.btn-modal-send:hover {
  background: #3f95ad;
  color: #fff;
}

.btn-modal-send:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.send-email-multiselect ::v-deep .multiselect__tags {
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid #d7dee8;
  padding: 6px 40px 6px 10px;
  box-shadow: none;
}

.send-email-multiselect ::v-deep .multiselect__tag {
  background: #edf6f9;
  color: #111827;
  border-radius: 999px;
  margin-bottom: 4px;
  font-size: 13px;
  padding: 6px 26px 6px 10px;
}

.send-email-multiselect ::v-deep .multiselect__tag-icon::after {
  color: #6b7280;
}

.send-email-multiselect ::v-deep .multiselect__input,
.send-email-multiselect ::v-deep .multiselect__placeholder {
  font-size: 15px;
  margin-bottom: 0;
}

.send-email-multiselect ::v-deep .multiselect__select {
  height: 44px;
}

.send-email-multiselect ::v-deep .multiselect__content-wrapper {
  border: 1px solid #d7dee8;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.12);
}

.send-email-multiselect ::v-deep .multiselect__option {
  font-size: 14px;
  padding: 10px 14px;
}

.send-email-multiselect ::v-deep .multiselect__option--highlight {
  background: #f8fafc;
  color: #111827;
}

.send-email-multiselect ::v-deep .multiselect__option--selected {
  background: #edf6f9;
  color: #111827;
}

.product-meta {
  min-width: 0;
}

.product-name {
  color: #111827;
  font-weight: 700;
  font-size: 15px;
  line-height: 1.2;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-brand {
  color: #677489;
  font-size: 15px;
  margin-top: 4px;
  line-height: 1.25;
  display: block;
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.custom-field-group-head {
  font-size: 12px !important;
}

.group-sub-head-row th {
  background: #f8fafc;
}

.custom-field-group-head {
  font-size: 13px !important;
  color: #334155 !important;
  font-weight: 700 !important;
  background: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  white-space: normal;
  line-height: 1.25;
  min-width: 120px;
}

.custom-field-child-head {
  font-size: 13px !important;
  color: #64748b !important;
  font-weight: 700 !important;
  background: #ffffff !important;
  white-space: nowrap;
}

.custom-field-sub-head-row th {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.dashboard-table thead th[rowspan="3"] {
  vertical-align: middle;
}

.date-picker {
  position: relative;
  width: 100%;
  max-width: 180px;
}

.date-picker-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1050;
  width: 292px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16);
}

.date-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 12px;
  border: 1px solid #d9e2ec;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.date-input-wrap:hover,
.date-input-wrap.is-open {
  border-color: #209b7b;
  box-shadow: 0 0 0 4px rgba(32, 155, 123, 0.12);
}

.date-input-wrap .bi-calendar3 {
  color: #209b7b;
  font-size: 16px;
}

.date-input {
  height: 40px;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  font-size: 14px;
  color: #102a43;
}

.date-input:focus {
  border: 0;
  box-shadow: none;
  outline: none;
}

.date-clear-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  cursor: pointer;
}

.date-clear-btn:hover {
  background: #e2e8f0;
  color: #334155;
}

.date-picker-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 1050;
  width: 292px;
  padding: 14px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.16);
}

.date-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.date-picker-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
}

.date-nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #475569;
  cursor: pointer;
}

.date-nav-btn:hover {
  background: #f8fafc;
  color: #0f172a;
}

.date-weekdays,
.date-days {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

.date-weekdays {
  margin-bottom: 6px;
}

.date-weekdays span {
  text-align: center;
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
}

.date-day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #334155;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.date-day:hover {
  background: #ecfdf5;
  color: #047857;
}

.date-day.is-muted {
  color: #cbd5e1;
}

.date-day.is-today {
  background: #f1f5f9;
  color: #0f172a;
}

.date-day.is-selected {
  background: #209b7b;
  color: #fff;
  box-shadow: 0 6px 14px rgba(32, 155, 123, 0.28);
}

.date-day.is-selected:hover {
  background: #188465;
  color: #fff;
}
.date-day.is-disabled,
.date-day:disabled {
  color: #cbd5e1;
  background: transparent;
  cursor: not-allowed;
  opacity: 0.45;
}

.date-day.is-disabled:hover,
.date-day:disabled:hover {
  background: transparent;
  color: #cbd5e1;
}

.date-day.is-selected:disabled {
  background: #94a3b8;
  color: #fff;
  box-shadow: none;
}

.product-thumb-wrap {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  margin-right: 12px;
  cursor: zoom-in;
}

.product-thumb {
  margin-right: 0;
}

.product-image-zoom {
  position: fixed;
  z-index: 3000;
  width: 280px;
  max-height: 320px;
  padding: 10px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #ffffff;
  box-shadow: 0 22px 55px rgba(15, 23, 42, 0.22);
  pointer-events: none;
}

.product-image-zoom img {
  display: block;
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 12px;
  background: #f8fafc;
}

.dashboard-load-more-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 18px 12px 8px;
}

.btn-load-more {
  border: 1px solid #d8dee9;
  background: #fff;
  border-radius: 999px;
  padding: 8px 18px;
  font-weight: 600;
  color: #334155;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.btn-load-more:hover:not(:disabled) {
  background: #f8fafc;
}

.btn-load-more:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.load-more-meta {
  font-size: 12px;
  color: #64748b;
}

.optimize-catalog-btn {
  border: 0;
  border-radius: 10px;
  font-weight: 600;
  line-height: 1;
  height: 36px;
  padding: 8px 16px !important;
  background-color: rgb(5, 150, 105);
}

.optimize-catalog-btn:hover {
  background-color: rgb(4, 120, 87);
}

.optimize-catalog-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.3);
}

.optimize-catalog-modal {
  position: relative;
  width: min(100%, 720px);
  height: min(90vh, 700px);
  border: 1px solid #dde5ef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 24px 56px rgba(15, 23, 42, 0.24);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.optimize-catalog-header {
  border-bottom-color: #e3e9f2;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.optimize-catalog-heading {
  text-align: left;
}

.optimize-catalog-title {
  color: #111827;
  font-size: 20px;
  line-height: 1.12;
  letter-spacing: -0.01em;
  font-weight: 700;
}

.optimize-catalog-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.2;
}

.optimize-catalog-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: #64748b;
}

.optimize-catalog-close:hover {
  background: #f1f5f9;
  color: #0f172a;
}

.optimize-select {
  width: 100%;
  height: 100%;
  border: 0;
  border-radius: 8px;
  background: #fff;
  padding: 0 28px 0 11px;
  font-size: 12px;
  color: #0f172a;
  font-weight: 500;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}

.optimize-select:focus {
  outline: none;
}

.optimize-select-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  border: 1px solid #d5dde8;
  border-radius: 8px;
  background: #fff;
}

.optimize-select-chevron {
  position: absolute;
  right: 8px;
  color: #7a8699;
  pointer-events: none;
}

.optimize-select-prefix {
  position: absolute;
  left: 9px;
  font-size: 12px;
  color: #8a96a9;
  pointer-events: none;
}

.optimize-select-wrap-owner .optimize-select,
.optimize-select-wrap-period .optimize-select {
  padding-left: 30px;
}

.optimize-dropdown-wrap {
  position: relative;
}

.optimize-dropdown-trigger {
  width: auto;
  min-height: 34px;
  border-style: dashed;
  padding: 0 28px 0 30px;
  text-align: left;
}

.optimize-dropdown-trigger-full {
  width: 100%;
}

.optimize-dropdown-trigger-plain {
  border-style: solid;
  padding-left: 11px;
}

.optimize-dropdown-value {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  color: #0f172a;
}

.optimize-dropdown-menu {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  z-index: 3001;
  width: 100%;
  min-width: 176px;
  border: 1px solid #d5dde8;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.16);
}

.optimize-dropdown-option {
  width: 100%;
  border: 0;
  background: transparent;
  color: #0f172a;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 6px;
  border-radius: 4px;
  min-height: 28px;
  padding: 4px 8px;
  font-size: 12px;
}

.optimize-dropdown-option:hover {
  background: #f1f5f9;
}

.optimize-dropdown-option.is-selected {
  background: #edf5f1;
  color: #0f172a;
}

.optimize-dropdown-option-indicator {
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.optimize-select-wide {
  min-width: 220px;
}

.optimize-product-dropdown {
  width: 33.3333%;
}

.optimize-select-issue-owner {
  min-width: 146px;
  margin-left: auto;
}

.optimize-select-footer {
  flex: 0 0 180px;
  width: 180px;
  min-width: 180px;
}

.optimize-catalog-issues-list {
  flex: 1;
  overflow-y: auto;
}

.optimize-issue-row {
  width: 100%;
  border: 1px solid #dbe4ef;
  border-radius: 10px;
  padding: 8px 12px;
  background: #fff;
  transition: all 0.2s ease;
}

.optimize-issue-main-row {
  flex-wrap: nowrap;
}

.optimize-issue-row.is-selected {
  border-color: #d5e8df;
  background: #edf5f1;
}

.optimize-issue-row.is-selected .optimize-issue-count {
  background: #dcebe3;
}

.optimize-issue-check {
  width: 20px;
  height: 20px;
  border: 2px solid #c2ccd9;
  border-radius: 5px;
  background: transparent;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.optimize-issue-check.is-selected {
  border-color: #059669;
  background: #059669;
}

.optimize-issue-content {
  border: 0;
  background: transparent;
  padding: 0;
}

.optimize-issue-icon {
  font-size: 15px;
  color: #64748b;
}

.optimize-issue-row.is-selected .optimize-issue-icon {
  color: #0d8d66;
}

.optimize-issue-count {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  background: #e7ebf1;
  color: #445064;
  font-size: 10px;
  font-weight: 500;
  padding: 1px 7px;
}


.optimize-catalog-footer {
  border-top: 1px solid #e2e8f0;
  background: #fbfcff;
  min-height: 66px;
  border-radius: 12px;
}

@media (max-width: 991px) {
  .optimize-catalog-title {
    font-size: 18px;
  }

  .optimize-catalog-subtitle {
    font-size: 13px;
  }

}

.space-y-1>:not([hidden])~:not([hidden]) {
  --tw-space-y-reverse: 0;
  margin-top: calc(.25rem * calc(1 - var(--tw-space-y-reverse)));
  margin-bottom: calc(.25rem * var(--tw-space-y-reverse));
}

.text-muted-foreground {
  color: hsl(var(--muted-foreground));
}
.gap-3 {
  gap: .75rem;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.flex {
  display: flex;
}
.px-6 {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
.py-3 {
  padding-top: .75rem;
  padding-bottom: .75rem;
}
.transition-all {
  transition-property: all;
  transition-timing-function: cubic-bezier(.4, 0, .2, 1);
  transition-duration: .15s;
}

.py-2\.5 {
  padding-top: .625rem;
  padding-bottom: .625rem;
}
.px-3 {
  padding-left: .75rem;
  padding-right: .75rem;
}
.bg-emerald-50\/30 {
  background-color: #ecfdf54d;
}
.border-emerald-500\/50 {
  border-color: #10b98180 !important;
}
.border {
  border-width: 1px;
  border-style: solid;
}
.rounded-lg {
  border-radius: var(--radius);
}
.w-full {
  width: 100%;
}
.space-y-1\.5>:not([hidden])~:not([hidden]) {
  --tw-space-y-reverse: 0;
  margin-top: calc(.375rem * calc(1 - var(--tw-space-y-reverse)));
  margin-bottom: calc(.375rem * var(--tw-space-y-reverse));
}
.flex-col {
  flex-direction: column;
}
.gap-1\.5 {
  gap: .375rem;
}

.optimize-btn-build-brief {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(.4, 0, .2, 1);
  transition-duration: .15s;
  font-weight: 500;
  font-size: .875rem;
  line-height: 1.25rem;
  padding-left: .75rem;
  padding-right: .75rem;
  background-color: hsl(var(--background));
  white-space: nowrap;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  height: 2.25rem;
  border: 1px solid #e2e2e2;
}

.optimize-btn-build-brief:hover {
  background-color: #f0f0f0;
}

.ml-auto {
  margin-left: auto;
}

.flex-wrap {
  flex-wrap: wrap;
}
.text-emerald-700 {
  --tw-text-opacity: 1;
  color: rgb(4 120 87 / var(--tw-text-opacity, 1));
}

.rounded-md {
  border-radius: 6px
}
.product-thumb-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: 1px dashed #d1d5db;
  color: #9ca3af;
  font-size: 9px;
  font-weight: 600;
  text-align: center;
  user-select: none;
}

.dashboard-header-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.shrink-0 {
  flex-shrink: 0;
}

.dashboard-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  line-height: 1.2;
}

.dashboard-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

.dashboard-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.kpi-center-slot {
  flex: 1 1 280px;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-cards-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
}

.kpi-card {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border-radius: 6px;
  border: 1px solid;
  font-size: 12px;
  cursor: pointer;
  transition: filter 0.15s;
  background: none;

  &:hover {
    filter: brightness(0.95);
  }

}

.kpi-card-amber {
  background: #d9ab2f;
  border-color: #d9ab2f;
  color: #ffffff;
}

.kpi-card-red {
  background: #e45858;
  border-color: #e45858;
  color: #ffffff;
}

.kpi-card-green {
  background: #58c06c;
  border-color: #58c06c;
  color: #ffffff;
}

.kpi-toggle-btn {
  height: 24px;
  padding: 0 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  background: transparent;
  font-size: 10px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  margin-left: 2px;

  &:hover {
    background: #f1f5f9;
  }
}

.kpi-expand-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px;
  border-radius: 9999px;
  border: 1px solid #e2e8ef;
  background-color: rgba(241, 245, 249, 0.4);
  font-size: 12px;
  font-weight: 500;
  color: #0f172a;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover {
    background-color: #f1f5f9;
  }
}

.kpi-expand-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 9999px;
  background-color: #fef3c7;
  color: #92400e;
  font-size: 10px;
  font-weight: 600;
}

.kpi-expand-chevron {
  color: #6b7280;
}

.kpi-collapse-btn {
  height: 28px;
  width: 28px;
  border-radius: 4px;
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
  padding: 0;

  &:hover {
    background: #f1f5f9;
  }
}

.optimize-split-btn {
  display: inline-flex;
  align-items: center;
  height: 36px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #065f46;
}

.optimize-split-main {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: 100%;
  background: #059669;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: none;

  &:hover {
    background: #047857;
  }
}

.optimize-split-clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  height: 100%;
  background: #059669;
  color: #fff;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.3);
  cursor: pointer;

  &:hover {
    background: #047857;
  }
}

.check-col {
  width: 36px;
  text-align: center;
  padding: 0 8px !important;

  input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    border: 1.5px solid hsl(168, 76%, 42%);
    border-radius: 3px;
    background: #fff;
    cursor: pointer;
    position: relative;
    vertical-align: middle;
    flex-shrink: 0;

    &:checked {
      background-color: hsl(168, 76%, 42%);
      border-color: hsl(168, 76%, 42%);

      &::after {
        content: '';
        position: absolute;
        left: 4px;
        top: 1px;
        width: 5px;
        height: 9px;
        border: 2px solid #fff;
        border-top: none;
        border-left: none;
        transform: rotate(45deg);
      }
    }

    &:focus-visible {
      outline: 2px solid hsl(168, 76%, 42%);
      outline-offset: 2px;
    }
  }
}

/* Bootstrap KPI tooltip */
.tooltip.kpi-dashboard-tooltip,
.tooltip:has(.kpi-tooltip-content) {
  pointer-events: none;
}

.tooltip:has(.kpi-tooltip-content) .tooltip-inner {
  max-width: 260px;
  padding: 0;
  border-radius: 10px;
  background: #111827;
  box-shadow:
    0 12px 28px rgba(15, 23, 42, 0.22),
    0 2px 8px rgba(15, 23, 42, 0.16);
  text-align: left;
  overflow: hidden;
}

.kpi-tooltip-content {
  min-width: 220px;
  padding: 11px 12px 10px;
}

.kpi-tooltip-title {
  margin-bottom: 8px;
  padding-bottom: 7px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.25;
}

.kpi-tooltip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 5px;
  color: #cbd5e1;
  font-size: 11px;
  line-height: 1.35;
}

.kpi-tooltip-row strong {
  color: #ffffff;
  font-weight: 700;
  white-space: nowrap;
}

.kpi-tooltip-context {
  margin-top: 8px;
  color: #94a3b8;
  font-size: 10px;
  line-height: 1.3;
}

.optimize-select-footer .optimize-select-wrap {
  border-style: solid;
}

button, input:where([type=button]), input:where([type=reset]), input:where([type=submit]) {
  -webkit-appearance: button;
  background-color: transparent;
  background-image: none;
}

.optimize-datepicker-cal {
  padding: 8px;
}

.optimize-datepicker-cal-header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 36px;
  margin-bottom: 6px;
}

.optimize-datepicker-cal-nav {
  position: absolute;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  border: 1px solid hsl(var(--border, 214 32% 91%));
  background: transparent;
  cursor: pointer;
  opacity: 0.5;
  padding: 0;
}

.optimize-datepicker-cal-nav:first-child {
  left: 4px;
}

.optimize-datepicker-cal-nav:last-child {
  right: 4px;
}

.optimize-datepicker-cal-nav:hover {
  opacity: 1;
  background: hsl(var(--accent, 210 40% 96%));
}

.optimize-datepicker-cal-month {
  font-size: 13px;
  font-weight: 500;
  color: #111827;
}

.optimize-datepicker-cal-weekdays {
  display: grid;
  grid-template-columns: repeat(7, 36px);
  margin-bottom: 4px;
}

.optimize-datepicker-cal-weekdays span {
  text-align: center;
  font-size: 11px;
  color: #94a3b8;
  padding: 2px 0;
}

.optimize-datepicker-cal-days {
  display: grid;
  grid-template-columns: repeat(7, 36px);
  gap: 2px;
}

.optimize-datepicker-cal-day {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  border-radius: 6px;
  background: transparent;
  border: 0;
  color: #0f172a;
  cursor: pointer;
  padding: 0;
  transition: background 0.1s;
}

.optimize-datepicker-cal-day:hover {
  background: hsl(var(--accent, 210 40% 96%));
  color: hsl(var(--accent-foreground, 222 47% 11%));
}

.optimize-datepicker-cal-day.is-other-month {
  color: #94a3b8;
  opacity: 0.5;
}

.optimize-datepicker-cal-day.is-today {
  font-weight: 600;
  color: #059669;
}

.optimize-datepicker-cal-day.is-selected {
  background: hsl(var(--accent, 210 40% 96%));
  color: hsl(var(--accent-foreground, 222 47% 11%));
  font-weight: 600;
}

/* ── POR-13699: utility classes for the redesigned optimize-catalog modal ── */

/* Display */
.inline-flex { display: inline-flex; }

/* Flex */
.flex-1 { flex: 1 1 0; }
.min-w-0 { min-width: 0; }
.justify-start { justify-content: flex-start; }
.justify-between { justify-content: space-between; }
.justify-end { justify-content: flex-end; }

/* Text */
.text-left { text-align: left; }
.whitespace-nowrap { white-space: nowrap; }
.truncate { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.font-normal { font-weight: 400; }
.font-medium { font-weight: 500; }
.font-semibold { font-weight: 600; }
.font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; }
.text-xs { font-size: 0.75rem; line-height: 1rem; }
.text-sm { font-size: 0.875rem; line-height: 1.25rem; }
.text-\[10px\] { font-size: 10px; }
.text-\[11px\] { font-size: 11px; }

/* Sizing */
.h-0\.5 { height: 2px; }
.h-3\.5 { height: 0.875rem; }
.h-4 { height: 1rem; }
.h-6 { height: 1.5rem; }
.h-7 { height: 1.75rem; }
.h-8 { height: 2rem; }
.h-9 { height: 2.25rem; }
.h-\[1px\] { height: 1px; }
.w-2 { width: 0.5rem; }
.w-3 { width: 0.75rem; }
.w-4 { width: 1rem; }
.w-64 { width: 16rem; }
.w-\[130px\] { width: 130px; }
.min-w-\[130px\] { min-width: 130px; }

/* Margin / padding */
.mt-1 { margin-top: 0.25rem; }
.mt-2 { margin-top: 0.5rem; }
.mt-3 { margin-top: 0.75rem; }
.ml-1 { margin-left: 0.25rem; }
.gap-0\.5 { gap: 0.125rem; }
.gap-1 { gap: 0.25rem; }
.gap-2 { gap: 0.5rem; }
.p-0\.5 { padding: 0.125rem; }
.p-1\.5 { padding: 0.375rem; }
.p-2 { padding: 0.5rem; }
.px-2 { padding-left: 0.5rem; padding-right: 0.5rem; }
.px-2\.5 { padding-left: 0.625rem; padding-right: 0.625rem; }
.px-1\.5 { padding-left: 0.375rem; padding-right: 0.375rem; }
.py-0 { padding-top: 0; padding-bottom: 0; }
.py-1 { padding-top: 0.25rem; padding-bottom: 0.25rem; }
.py-1\.5 { padding-top: 0.375rem; padding-bottom: 0.375rem; }
.py-2 { padding-top: 0.5rem; padding-bottom: 0.5rem; }
.space-y-2 > :not([hidden]) ~ :not([hidden]) {
  --tw-space-y-reverse: 0;
  margin-top: calc(0.5rem * calc(1 - var(--tw-space-y-reverse)));
  margin-bottom: calc(0.5rem * var(--tw-space-y-reverse));
}

/* Border */
.border { border-width: 1px; border-style: solid; }
.border-dashed { border-style: dashed; }
.rounded { border-radius: 4px; }
.rounded-full { border-radius: 9999px; }
.rounded-\[5px\] { border-radius: 5px; }
.border-transparent { border-color: transparent; }
.border-input { border-color: hsl(var(--input, 214 32% 91%)); }
.border-border { border-color: hsl(var(--border, 214 32% 91%)); }
.border-emerald-400 { border-color: #34d399 !important; }
.border-emerald-600 { border-color: #059669 !important; }

/* Position */
.relative { position: relative; }
.absolute { position: absolute; }
.top-full { top: 100%; }
.left-0 { left: 0; }
.z-50 { z-index: 50; }

/* Shadow */
.shadow-md {
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}

/* Background */
.bg-background { background-color: hsl(var(--background, 0 0% 100%)); }
.bg-popover { background-color: hsl(var(--popover, 0 0% 100%)); }
.bg-muted { background-color: hsl(var(--muted, 210 40% 96%)); }
.bg-muted\/30 { background-color: hsl(var(--muted, 210 40% 96%) / 0.3); }
.bg-white { background-color: #ffffff; }
.bg-border { background-color: hsl(var(--border, 214 32% 91%)); }
.bg-emerald-50 { background-color: #ecfdf5; }
.bg-emerald-100 { background-color: #d1fae5; }
.bg-emerald-600 { background-color: #059669; }
.bg-red-100 { background-color: #fee2e2; }
.bg-amber-100 { background-color: #fef3c7; }

/* Text color */
.text-foreground { color: hsl(var(--foreground, 222 47% 11%)); }
.text-popover-foreground { color: hsl(var(--popover-foreground, 222 47% 11%)); }
.text-white { color: #ffffff; }
.text-red-700 { color: #b91c1c; }
.text-amber-700 { color: #b45309; }

/* Hover states */
.hover\:bg-accent:hover { background-color: hsl(var(--accent, 210 40% 96%)); }
.hover\:text-accent-foreground:hover { color: hsl(var(--accent-foreground, 222 47% 11%)); }
.hover\:bg-muted:hover { background-color: hsl(var(--muted, 210 40% 96%)); }
.hover\:bg-emerald-100:hover { background-color: #d1fae5; }
.hover\:bg-emerald-700:hover { background-color: #047857; }
.hover\:text-foreground:hover { color: hsl(var(--foreground, 222 47% 11%)); }

/* Transition */
.transition-colors {
  transition-property: color, background-color, border-color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
.refresh-confirm-overlay {
  position: fixed;
  inset: 0;
  z-index: 2200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.3);
}

.refresh-confirm-modal {
  width: min(100%, 420px);
  padding: 20px 22px 18px;
  border: 1px solid #dde5ef;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 24px 56px rgba(15, 23, 42, 0.24);
}

.refresh-confirm-title {
  margin: 0 0 8px;
  color: #111827;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.25;
}

.refresh-confirm-text {
  margin: 0 0 18px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.45;
}

.refresh-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.refresh-confirm-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.refresh-confirm-btn--cancel {
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #334155;
}

.refresh-confirm-btn--cancel:hover {
  background: #f8fafc;
}

.refresh-confirm-btn--primary {
  border: 1px solid #065f46;
  background: #059669;
  color: #fff;
}

.refresh-confirm-btn--primary:hover {
  background: #047857;
}
</style>
